import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import API_BASE from './apiBase';
import maskUtils from './maskUtils';

const { maskEmail, maskPhone } = maskUtils;

// ─── Browser SVG Icons ────────────────────────────────────────────────────────
const BrowserIcons = {
  chrome: (
    <svg width="22" height="22" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="10.5" fill="white" />
      <path d="M12 1.5 A10.5 10.5 0 0 1 21.59 6.75 L12 6.75 Z" fill="#EA4335" />
      <path d="M21.59 6.75 A10.5 10.5 0 0 1 21.59 17.25 L16.795 12 L21.59 6.75 Z" fill="#FBBC05" />
      <path d="M21.59 17.25 A10.5 10.5 0 0 1 2.41 17.25 L7.205 12 L21.59 17.25 Z" fill="#34A853" />
      <path d="M2.41 17.25 A10.5 10.5 0 0 1 2.41 6.75 L7.205 12 Z" fill="#4285F4" />
      <path d="M2.41 6.75 A10.5 10.5 0 0 1 12 1.5 L12 6.75 L2.41 6.75 Z" fill="#EA4335" />
      <circle cx="12" cy="12" r="4" fill="#4285F4" />
      <circle cx="12" cy="12" r="2.5" fill="white" />
    </svg>
  ),
  firefox: (
    <svg width="22" height="22" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <radialGradient id="ffBg" cx="40%" cy="40%">
          <stop offset="0%" stopColor="#FF9800" />
          <stop offset="60%" stopColor="#FF5722" />
          <stop offset="100%" stopColor="#BF360C" />
        </radialGradient>
        <radialGradient id="ffGlobe" cx="50%" cy="40%">
          <stop offset="0%" stopColor="#42A5F5" />
          <stop offset="100%" stopColor="#0D47A1" />
        </radialGradient>
      </defs>
      <circle cx="12" cy="12" r="10.5" fill="url(#ffGlobe)" />
      <path d="M12 1.5c-5.8 0-10.5 4.7-10.5 10.5S6.2 22.5 12 22.5c1.6 0 3.1-.4 4.5-1-3-1.5-5-4.6-5-8 0-2 .6-3.8 1.7-5.3-.5-.1-1.1-.2-1.7-.2-2.3 0-4.3 1-5.7 2.6C7 8.9 8.2 7 10 6c.7-1.5 2.1-2.5 3.8-2.5.9 0 1.8.3 2.5.8C15 3.5 13.6 1.5 12 1.5z" fill="url(#ffBg)" />
    </svg>
  ),
  safari: (
    <svg width="22" height="22" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="safBg" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#D0E8FF" />
          <stop offset="100%" stopColor="#A8CFFF" />
        </linearGradient>
      </defs>
      <circle cx="12" cy="12" r="10.5" fill="url(#safBg)" />
      <polygon points="12,5 13,11.5 12,12.5 11,11.5" fill="#FF3B30" />
      <polygon points="12,19 13,12.5 12,11.5 11,12.5" fill="white" />
      <circle cx="12" cy="12" r="1.2" fill="#334155" />
    </svg>
  ),
  edge: (
    <svg width="22" height="22" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="edgeGrad1" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#0078D4" />
          <stop offset="100%" stopColor="#0050A0" />
        </linearGradient>
        <linearGradient id="edgeGrad2" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#50E6FF" />
          <stop offset="100%" stopColor="#1BB8FF" />
        </linearGradient>
      </defs>
      <circle cx="12" cy="12" r="10.5" fill="url(#edgeGrad1)" />
      <path d="M4 11.5c0-3.3 2.4-6.5 6-7.5 2-.6 4.1-.3 5.8.7 2.2 1.3 3.2 3.5 3.2 5.8 0 .7-.1 1-.1 1H6.5c.2 2.8 2.5 4.5 5 4.5 1.8 0 3.2-.8 4.2-2l1.8 1.5C16 17.5 14 19 11.5 19 7.4 19 4 15.6 4 11.5z" fill="url(#edgeGrad2)" />
    </svg>
  ),
  ie: (
    <svg width="22" height="22" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="10.5" fill="#1EBBEE" />
      <text x="12" y="16" textAnchor="middle" fontSize="11" fontWeight="900" fill="white" fontFamily="serif" fontStyle="italic">e</text>
    </svg>
  ),
  opera: (
    <svg width="22" height="22" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="opGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#FF4040" />
          <stop offset="100%" stopColor="#CC0F16" />
        </linearGradient>
      </defs>
      <circle cx="12" cy="12" r="10.5" fill="url(#opGrad)" />
      <ellipse cx="12" cy="12" rx="5" ry="7.5" fill="none" stroke="rgba(255,255,255,0.9)" strokeWidth="2.5" />
    </svg>
  ),
  brave: (
    <svg width="22" height="22" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="10.5" fill="#FB542B" />
      <path d="M12 3.5L18.5 6.5l-1 9L12 20.5 6.5 15.5l-1-9Z" fill="white" opacity="0.95" />
      <path d="M9.5 10h5l-.8 4.5L12 16l-1.7-1.5Z" fill="#FB542B" />
    </svg>
  ),
  vivaldi: (
    <svg width="22" height="22" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="10.5" fill="#EF3939" />
      <path d="M12 4L17.5 16.5H6.5Z" fill="white" opacity="0.9" />
      <circle cx="12" cy="13" r="3" fill="#EF3939" />
    </svg>
  ),
  arc: (
    <svg width="22" height="22" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="arcGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#FF6B9D" />
          <stop offset="50%" stopColor="#A855F7" />
          <stop offset="100%" stopColor="#3B82F6" />
        </linearGradient>
      </defs>
      <circle cx="12" cy="12" r="10.5" fill="url(#arcGrad)" />
      <path d="M7 16 Q12 5 17 16" stroke="white" strokeWidth="2.2" fill="none" strokeLinecap="round" />
      <line x1="9.5" y1="13" x2="14.5" y2="13" stroke="white" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  ),
  samsung: (
    <svg width="22" height="22" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="10.5" fill="#1428A0" />
      <text x="12" y="15.5" textAnchor="middle" fontSize="7" fontWeight="700" fill="white" fontFamily="sans-serif">S</text>
    </svg>
  ),
  uc: (
    <svg width="22" height="22" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="10.5" fill="#FF6600" />
      <text x="12" y="15.5" textAnchor="middle" fontSize="6.5" fontWeight="700" fill="white" fontFamily="sans-serif">UC</text>
    </svg>
  ),
  tor: (
    <svg width="22" height="22" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="10.5" fill="#7D4698" />
      <circle cx="12" cy="12" r="6" fill="none" stroke="white" strokeWidth="1.5" />
      <circle cx="12" cy="12" r="3" fill="white" />
      <circle cx="12" cy="5.5" r="1.5" fill="white" />
    </svg>
  ),
  default: (
    <svg width="22" height="22" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="10.5" fill="#94a3b8" />
      <path d="M2 12h20M12 2a15.3 15.3 0 0 1 0 20M12 2a15.3 15.3 0 0 0 0 20" stroke="white" strokeWidth="1.4" fill="none" />
      <circle cx="12" cy="12" r="10.5" fill="none" stroke="white" strokeWidth="1.4" />
    </svg>
  ),
};

function getBrowserKey(browserStr) {
  if (!browserStr || browserStr === '-') return 'default';
  const b = String(browserStr).toLowerCase();
  if (b.includes('brave')) return 'brave';
  if (b.includes('vivaldi')) return 'vivaldi';
  if (b.includes('arc')) return 'arc';
  if (b.includes('edg')) return 'edge';
  if (b.includes('opr') || b.includes('opera')) return 'opera';
  if (b.includes('samsung')) return 'samsung';
  if (b.includes('ucbrowser') || b.includes('ucweb')) return 'uc';
  if (b.includes('tor')) return 'tor';
  if (b.includes('chrome') || b.includes('crios') || b.includes('chromium')) return 'chrome';
  if (b.includes('firefox') || b.includes('fxios')) return 'firefox';
  if (b.includes('safari')) return 'safari';
  if (b.includes('msie') || b.includes('trident')) return 'ie';
  return 'default';
}

function getBrowserName(browserStr) {
  if (!browserStr || browserStr === '-') return 'Unknown Browser';
  const b = String(browserStr).toLowerCase();
  if (b.includes('brave')) return 'Brave';
  if (b.includes('vivaldi')) return 'Vivaldi';
  if (b.includes('arc')) return 'Arc';
  if (b.includes('edg')) return 'Microsoft Edge';
  if (b.includes('opr') || b.includes('opera')) return 'Opera';
  if (b.includes('samsung')) return 'Samsung Internet';
  if (b.includes('ucbrowser') || b.includes('ucweb')) return 'UC Browser';
  if (b.includes('tor')) return 'Tor Browser';
  if (b.includes('crios')) return 'Chrome (iOS)';
  if (b.includes('chrome') || b.includes('chromium')) return 'Chrome';
  if (b.includes('fxios')) return 'Firefox (iOS)';
  if (b.includes('firefox')) return 'Firefox';
  if (b.includes('safari')) return 'Safari';
  if (b.includes('msie') || b.includes('trident')) return 'Internet Explorer';
  const first = String(browserStr).split(/[\s/]/)[0];
  return first || 'Browser';
}

// ─── Status Badge ─────────────────────────────────────────────────────────────
const STATUS_CONFIG = {
  accepted: {
    bg: '#dcfce7', border: '#86efac', color: '#15803d', label: 'Accepted', icon: (
      <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
        <path d="M2.5 6.5l2.5 2.5 4.5-5" stroke="#15803d" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    )
  },
  revoked: {
    bg: '#fef3c7', border: '#fcd34d', color: '#b45309', label: 'Revoked', icon: (
      <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
        <circle cx="6" cy="6" r="4.5" stroke="#b45309" strokeWidth="1.6" />
        <path d="M4 6h4" stroke="#b45309" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    )
  },
  declined: {
    bg: '#fee2e2', border: '#fca5a5', color: '#dc2626', label: 'Declined', icon: (
      <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
        <path d="M3 3l6 6M9 3l-6 6" stroke="#dc2626" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    )
  },
  pending: {
    bg: '#eff6ff', border: '#93c5fd', color: '#2563eb', label: 'Pending', icon: (
      <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
        <circle cx="6" cy="6" r="4.5" stroke="#2563eb" strokeWidth="1.6" />
        <path d="M6 3.5v2.8l1.5 1.5" stroke="#2563eb" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    )
  },
  unknown: {
    bg: '#f1f5f9', border: '#cbd5e1', color: '#64748b', label: 'Unknown', icon: (
      <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
        <circle cx="6" cy="6" r="4.5" stroke="#64748b" strokeWidth="1.6" />
        <path d="M6 8.5v.3" stroke="#64748b" strokeWidth="2" strokeLinecap="round" />
        <path d="M4.5 4.8c0-1 .7-1.5 1.5-1.5s1.5.6 1.5 1.5c0 .8-.6 1.2-1.5 1.5" stroke="#64748b" strokeWidth="1.4" strokeLinecap="round" />
      </svg>
    )
  },
};

function getStatusKey(statusStr) {
  const s = String(statusStr || '').toLowerCase().trim();
  if (s.includes('accept') || s === 'granted' || s === 'approved') return 'accepted';
  if (s.includes('revok')) return 'revoked';
  if (s.includes('declin') || s.includes('reject') || s.includes('denied') || s.includes('deny')) return 'declined';
  if (s.includes('pend') || s.includes('wait') || s === '') return 'pending';
  return 'unknown';
}

// ─── Browser Cell ─────────────────────────────────────────────────────────────
function BrowserCell({ browserRaw }) {
  const key = getBrowserKey(browserRaw);
  const name = getBrowserName(browserRaw);
  return (
    <span title={browserRaw || 'Unknown'} style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '3px 9px 3px 5px', borderRadius: 999,
      background: '#f8fafc', border: '1px solid #e2e8f0',
      fontSize: 11.5, fontWeight: 500, color: '#374151',
      whiteSpace: 'nowrap', userSelect: 'none', cursor: 'default',
    }}>
      <span style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
        {BrowserIcons[key]}
      </span>
      {name}
    </span>
  );
}

// ─── Email Status Icon ────────────────────────────────────────────────────────
function EmailStatusIcon({ status }) {
  const s = String(status || '').toLowerCase();
  if (s.includes('revok')) {
    return (
      <span title="Revoked" style={{ display: 'inline-flex', alignItems: 'center' }}>
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"
          fill="none" stroke="#b45309" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="8" stroke="#b45309" />
          <line x1="15" y1="9" x2="9" y2="15" stroke="#b45309" />
          <line x1="9" y1="9" x2="15" y2="15" stroke="#b45309" />
        </svg>
      </span>
    );
  }
  let color = '#f59e0b';
  let title = 'Email: Pending';
  if (s.includes('accept') || s.includes('granted') || s.includes('approved')) { color = '#16a34a'; title = 'Email: Accepted'; }
  else if (s.includes('declin') || s.includes('reject')) { color = '#dc2626'; title = 'Email: Declined'; }
  return (
    <span title={title} style={{ display: 'inline-flex', alignItems: 'center' }}>
      <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <rect x="2" y="4" width="20" height="14" rx="2" fill={color} />
        <path d="M22 6l-10 7L2 6" fill="#fff" opacity="0.9" />
      </svg>
    </span>
  );
}

const parseTimestampAsUTC = (value) => {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) return value;
  const timestamp = String(value).trim();
  const naiveIso = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/;
  return new Date(naiveIso.test(timestamp) ? `${timestamp.replace(' ', 'T')}Z` : timestamp);
};

const formatIndianDateTime = (value) => {
  if (!value) return '-';
  const date = parseTimestampAsUTC(value);
  if (!date || Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZone: 'Asia/Kolkata',
  }).format(date);
};

const formatActivityDetails = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  let details = value;
  if (typeof details === 'string') {
    try {
      details = JSON.parse(details);
    } catch {
      return details;
    }
  }
  if (typeof details !== 'object' || Array.isArray(details)) return String(details);
  return Object.entries(details)
    .filter(([, entry]) => entry !== null && entry !== undefined && entry !== '')
    .map(([key, entry]) => `${key.replace(/_/g, ' ')}: ${typeof entry === 'object' ? JSON.stringify(entry) : String(entry)}`)
    .join(' · ') || '-';
};

// ─── Main Component ───────────────────────────────────────────────────────────
export default function UserLogsPage({ user, onBack, onMenu }) {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [loadError, setLoadError] = useState('');
  const [historyLoadingId, setHistoryLoadingId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [usernameFilter, setUsernameFilter] = useState('');
  const [emailFilter, setEmailFilter] = useState('');

  // ─── CSV Status Modal State ────────────────────────────────────────────────
  const [showCsvStatusModal, setShowCsvStatusModal] = useState(false);
  const [csvStatusLoading, setCsvStatusLoading] = useState(false);
  const [csvStatusRefreshing, setCsvStatusRefreshing] = useState(false);
  const [csvStatusError, setCsvStatusError] = useState('');
  const [csvJobs, setCsvJobs] = useState([]);
  const [selectedUploadId, setSelectedUploadId] = useState('');
  const [selectedJobDetails, setSelectedJobDetails] = useState(null);
  const [resumingUploadId, setResumingUploadId] = useState(null);
  const [cancellingUploadId, setCancellingUploadId] = useState(null);
  const [deletingUploadId, setDeletingUploadId] = useState(null);
  const [cleaningQueue, setCleaningQueue] = useState(false);
  const [showCleanupMenu, setShowCleanupMenu] = useState(false);
  const [csvRecordSearch, setCsvRecordSearch] = useState('');
  const [csvRecordStatusFilter, setCsvRecordStatusFilter] = useState('all');
  const [rejectingRecordId, setRejectingRecordId] = useState(null);
  const [csvCardFilter, setCsvCardFilter] = useState('all');
  const [csvCardSearch, setCsvCardSearch] = useState('');
  const pollingRef = useRef(null);

  const filteredCsvJobs = useMemo(() => {
    if (!csvJobs || !Array.isArray(csvJobs)) return [];
    return csvJobs.filter((j) => {
      const s = String(j.status || 'queued').toLowerCase();
      const name = String(j.filename || '').toLowerCase();
      const q = csvCardSearch.toLowerCase().trim();
      if (q && !name.includes(q)) return false;

      if (csvCardFilter === 'all') return true;
      if (csvCardFilter === 'processing') return ['processing', 'queued', 'uploading'].includes(s);
      if (csvCardFilter === 'completed') return ['done', 'completed', 'finished'].includes(s);
      if (csvCardFilter === 'paused') return ['paused', 'cancelled'].includes(s);
      if (csvCardFilter === 'failed') return s === 'failed';
      return true;
    });
  }, [csvJobs, csvCardFilter, csvCardSearch]);

  const userTenantId = user ? (user.tenant_id || user.tenantId || user.tenant || null) : null;
  const getEffectiveTenantId = useCallback(() => {
    let tenantId = userTenantId;
    const token = localStorage.getItem('token') || sessionStorage.getItem('token') || '';
    if (tenantId === null && token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        tenantId = payload.tenant_id || payload.tenant || payload.tid || null;
      } catch (_) { }
    }
    if (tenantId === null) {
      const storedTenant = localStorage.getItem('tenant_id') || sessionStorage.getItem('tenant_id');
      if (storedTenant) tenantId = Number(storedTenant) || null;
    }
    return tenantId;
  }, [userTenantId]);

  const fetchCsvJobDetails = useCallback(async (uploadId, allJobs = null, recordLimit = 50) => {
    if (!uploadId) return;
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token') || '';
      const authHeaders = {
        Accept: 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };
      const apiBase = String(API_BASE || '').replace(/\/+$/, '');
      const fallbackBase = apiBase.replace(/\/api$/, '');
      const tenantId = getEffectiveTenantId();
      const tenantParam = tenantId ? `&tenant_id=${tenantId}` : '';

      let summaryData = null;
      const detailUrls = [
        `${apiBase}/bulk/uploads/${uploadId}?record_limit=${recordLimit}${tenantParam}`,
        `${fallbackBase}/bulk/uploads/${uploadId}?record_limit=${recordLimit}${tenantParam}`,
        `${apiBase}/api/bulk/uploads/${uploadId}?record_limit=${recordLimit}${tenantParam}`,
      ];
      for (const url of detailUrls) {
        try {
          const res = await axios.get(url, { headers: authHeaders, timeout: 10000 });
          if (res.data) {
            summaryData = res.data;
            break;
          }
        } catch (_) { }
      }

      const jobsList = allJobs || csvJobs;
      const matchedJob = jobsList.find(j => j.upload_id === uploadId);
      const jobId = summaryData?.upload?.job_id || matchedJob?.job_id;

      let liveData = null;
      if (jobId) {
        const liveUrls = [
          `${apiBase}/bulk/jobs/${jobId}`,
          `${fallbackBase}/bulk/jobs/${jobId}`,
          `${apiBase}/api/bulk/jobs/${jobId}`,
        ];
        for (const url of liveUrls) {
          try {
            const res = await axios.get(url, { headers: authHeaders, timeout: 8000 });
            if (res.data) {
              liveData = res.data;
              break;
            }
          } catch (_) { }
        }
      }

      setSelectedJobDetails({
        upload: summaryData?.upload || matchedJob || {},
        chunks: summaryData?.chunks || [],
        records: summaryData?.records || [],
        live: liveData,
      });

      // Keep job list card synced with live progress
      if (liveData || summaryData?.upload) {
        setCsvJobs(prevJobs => prevJobs.map(job => {
          if (job.upload_id === uploadId) {
            const up = summaryData?.upload || {};
            const live = liveData || {};
            const saved = Number(live.inserted !== undefined ? live.inserted : (up.inserted_rows || up.successful_rows || job.successful_rows || 0));
            const failed = Number(live.failed_rows !== undefined ? live.failed_rows : (up.failed_rows !== undefined ? up.failed_rows : job.failed_rows || 0));
            const tot = Number(up.total_rows || live.total_records || job.total_rows || 0);
            return {
              ...job,
              status: live.status || up.status || job.status,
              total_rows: tot,
              successful_rows: saved,
              failed_rows: failed,
              processed_rows: Number(live.processed_rows || up.processed_rows || job.processed_rows || 0),
              pending_rows: Math.max(0, tot - (saved + failed)),
            };
          }
          return job;
        }));
      }

      setCsvStatusError('');
    } catch (err) {
      console.error('Error fetching CSV job details:', err);
    }
  }, [csvJobs, getEffectiveTenantId]);

  const fetchCsvStatusList = useCallback(async (targetUploadId = null, isRefresh = false) => {
    if (isRefresh) setCsvStatusRefreshing(true);
    else setCsvStatusLoading(true);

    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token') || '';
      const authHeaders = {
        Accept: 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };
      const apiBase = String(API_BASE || '').replace(/\/+$/, '');
      const fallbackBase = apiBase.replace(/\/api$/, '');
      const tenantId = getEffectiveTenantId();
      const tenantQuery = tenantId ? `&tenant_id=${tenantId}` : '';

      let uploadsList = [];
      const primaryUrl = `${apiBase}/bulk/uploads?limit=50${tenantQuery}`;
      const fallbackUrl1 = `${fallbackBase}/bulk/uploads?limit=50${tenantQuery}`;
      const fallbackUrl2 = `${apiBase}/api/bulk/uploads?limit=50${tenantQuery}`;

      let fetchError = null;
      for (const url of [primaryUrl, fallbackUrl1, fallbackUrl2]) {
        try {
          const res = await axios.get(url, { headers: authHeaders, timeout: 10000 });
          if (res.data?.items && Array.isArray(res.data.items)) {
            uploadsList = res.data.items;
            break;
          }
        } catch (err) {
          fetchError = err;
        }
      }

      setCsvJobs(uploadsList);

      const resolvedUploadId = targetUploadId || selectedUploadId || (uploadsList.length > 0 ? uploadsList[0].upload_id : '');
      if (resolvedUploadId) {
        setSelectedUploadId(resolvedUploadId);
        await fetchCsvJobDetails(resolvedUploadId, uploadsList);
      } else {
        setSelectedJobDetails(null);
      }
      if (uploadsList.length > 0 || !fetchError) {
        setCsvStatusError('');
      } else if (fetchError) {
        setCsvStatusError(fetchError?.response?.data?.detail || fetchError?.message || 'Unable to load CSV status list');
      }
    } catch (err) {
      console.error('Failed to load CSV jobs list:', err);
      setCsvStatusError(err?.response?.data?.detail || err?.message || 'Unable to load CSV status list');
    } finally {
      setCsvStatusLoading(false);
      setCsvStatusRefreshing(false);
    }
  }, [selectedUploadId, fetchCsvJobDetails, getEffectiveTenantId]);

  // ── SweetAlert Top Helpers (Guaranteed front layer, never hidden behind modals) ──
  const showTopToast = useCallback((icon, title, text = '') => {
    return Swal.fire({
      icon,
      title,
      text: text || undefined,
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: 2600,
      timerProgressBar: true,
      target: document.body,
      customClass: {
        container: 'swal2-always-top-layer',
        popup: 'swal2-always-top-popup',
      },
      didOpen: (toast) => {
        toast.style.zIndex = '2147483647';
        if (toast.parentElement) {
          toast.parentElement.style.zIndex = '2147483647';
        }
      },
    });
  }, []);

  const showTopConfirm = useCallback((options = {}) => {
    return Swal.fire({
      ...options,
      target: document.body,
      customClass: {
        container: 'swal2-always-top-layer',
        popup: 'swal2-always-top-popup',
        ...(options.customClass || {}),
      },
      didOpen: (popup) => {
        popup.style.zIndex = '2147483647';
        if (popup.parentElement) {
          popup.parentElement.style.zIndex = '2147483647';
        }
        if (options.didOpen) options.didOpen(popup);
      },
    });
  }, []);

  const handleResumeUpload = async (uploadId) => {
    if (!uploadId || resumingUploadId) return;
    try {
      setResumingUploadId(uploadId);
      // Optimistic UI update so card reflects queued immediately without page freeze
      setCsvJobs((prev) =>
        prev.map((j) => (j.upload_id === uploadId ? { ...j, status: 'queued' } : j))
      );
      showTopToast('success', 'Processing Started / Resumed', 'Upload job enqueued. Progress updating live.');

      const token = localStorage.getItem('token') || sessionStorage.getItem('token') || '';
      const authHeaders = {
        Accept: 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };
      const apiBase = String(API_BASE || '').replace(/\/+$/, '');
      const fallbackBase = apiBase.replace(/\/api$/, '');
      const tenantId = getEffectiveTenantId();
      const tenantParam = tenantId ? `?tenant_id=${tenantId}` : '';

      let resumeRes = null;
      let lastError = null;
      const resumeUrls = [
        `${apiBase}/bulk/uploads/${uploadId}/resume${tenantParam}`,
        `${fallbackBase}/bulk/uploads/${uploadId}/resume${tenantParam}`,
        `${apiBase}/api/bulk/uploads/${uploadId}/resume${tenantParam}`,
      ];
      for (const url of resumeUrls) {
        try {
          resumeRes = await axios.post(url, {}, { headers: authHeaders, timeout: 15000 });
          if (resumeRes?.data) break;
        } catch (err) {
          lastError = err;
          if (err?.response?.status === 404 || err?.response?.status === 410 || err?.response?.status === 403) {
            break;
          }
        }
      }

      if (resumeRes?.data) {
        await fetchCsvStatusList(uploadId, true);
      } else {
        const errorDetail =
          lastError?.response?.data?.detail ||
          lastError?.message ||
          'Upload resumption could not be initiated.';
        throw new Error(errorDetail);
      }
    } catch (err) {
      console.error('Failed to resume upload:', err);
      showTopToast('error', 'Resume / Restart Failed', err?.message || 'Unable to resume upload processing.');
      await fetchCsvStatusList(uploadId, true);
    } finally {
      setResumingUploadId(null);
    }
  };

  const handleCancelUpload = async (uploadId) => {
    if (!uploadId || cancellingUploadId) return;
    try {
      setCancellingUploadId(uploadId);
      // Optimistic UI update
      setCsvJobs((prev) =>
        prev.map((j) => (j.upload_id === uploadId ? { ...j, status: 'cancelled' } : j))
      );
      showTopToast('info', 'Upload Paused / Stopped', 'Upload processing paused. You can resume anytime.');

      const token = localStorage.getItem('token') || sessionStorage.getItem('token') || '';
      const authHeaders = {
        Accept: 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };
      const apiBase = String(API_BASE || '').replace(/\/+$/, '');
      const fallbackBase = apiBase.replace(/\/api$/, '');
      const tenantId = getEffectiveTenantId();
      const tenantParam = tenantId ? `?tenant_id=${tenantId}` : '';

      const cancelUrls = [
        `${apiBase}/bulk/uploads/${uploadId}/cancel${tenantParam}`,
        `${fallbackBase}/bulk/uploads/${uploadId}/cancel${tenantParam}`,
        `${apiBase}/api/bulk/uploads/${uploadId}/cancel${tenantParam}`,
      ];

      let res = null;
      let lastError = null;
      for (const url of cancelUrls) {
        try {
          res = await axios.post(url, {}, { headers: authHeaders, timeout: 15000 });
          if (res?.data) break;
        } catch (err) {
          lastError = err;
          if (err?.response?.status === 404 || err?.response?.status === 403) {
            break;
          }
        }
      }

      if (res?.data) {
        await fetchCsvStatusList(uploadId, true);
      } else {
        const errorDetail =
          lastError?.response?.data?.detail ||
          lastError?.message ||
          'Could not pause / stop upload.';
        throw new Error(errorDetail);
      }
    } catch (err) {
      console.error('Failed to cancel upload:', err);
      showTopToast('error', 'Action Failed', err?.message || 'Unable to pause upload.');
      await fetchCsvStatusList(uploadId, true);
    } finally {
      setCancellingUploadId(null);
    }
  };

  const handleDeleteUpload = async (uploadId) => {
    if (!uploadId || deletingUploadId) return;
    try {
      const confirmResult = await showTopConfirm({
        title: 'Delete This Upload?',
        text: 'This will permanently remove this CSV upload and its tracking logs from the queue and database.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, Delete Completely',
        cancelButtonText: 'Cancel',
        confirmButtonColor: '#dc2626',
      });
      if (!confirmResult.isConfirmed) return;

      setDeletingUploadId(uploadId);
      // Remove from UI state immediately
      setCsvJobs((prev) => prev.filter((j) => j.upload_id !== uploadId));
      if (selectedUploadId === uploadId) {
        const remaining = csvJobs.filter((j) => j.upload_id !== uploadId);
        if (remaining.length > 0) {
          setSelectedUploadId(remaining[0].upload_id);
          fetchCsvJobDetails(remaining[0].upload_id);
        } else {
          setSelectedUploadId('');
          setSelectedJobDetails(null);
        }
      }
      showTopToast('success', 'Upload Deleted', 'CSV upload and tracking logs permanently removed.');

      const token = localStorage.getItem('token') || sessionStorage.getItem('token') || '';
      const authHeaders = {
        Accept: 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };
      const apiBase = String(API_BASE || '').replace(/\/+$/, '');
      const fallbackBase = apiBase.replace(/\/api$/, '');
      const tenantId = getEffectiveTenantId();
      const tenantParam = tenantId ? `?tenant_id=${tenantId}` : '';

      const delUrls = [
        `${apiBase}/bulk/uploads/${uploadId}${tenantParam}`,
        `${fallbackBase}/bulk/uploads/${uploadId}${tenantParam}`,
        `${apiBase}/api/bulk/uploads/${uploadId}${tenantParam}`,
      ];

      let res = null;
      let lastError = null;
      for (const url of delUrls) {
        try {
          res = await axios.delete(url, { headers: authHeaders, timeout: 15000 });
          if (res?.data) break;
        } catch (err) {
          lastError = err;
          if (err?.response?.status === 404 || err?.response?.status === 403) {
            break;
          }
        }
      }

      if (res?.data) {
        await fetchCsvStatusList(null, true);
      } else {
        const errorDetail =
          lastError?.response?.data?.detail ||
          lastError?.message ||
          'Unable to delete upload.';
        throw new Error(errorDetail);
      }
    } catch (err) {
      console.error('Failed to delete upload:', err);
      showTopToast('error', 'Delete Failed', err?.message || 'Unable to delete upload.');
      await fetchCsvStatusList(null, true);
    } finally {
      setDeletingUploadId(null);
    }
  };

  const handleDeleteFailedRecords = async (uploadId) => {
    if (!uploadId) return;
    try {
      const confirmResult = await showTopConfirm({
        title: 'Remove Failed Records?',
        text: 'Are you sure you want to remove all failed records from this upload dataset?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, Remove Failed Records',
        cancelButtonText: 'Cancel',
        confirmButtonColor: '#dc2626',
      });
      if (!confirmResult.isConfirmed) return;

      const token = localStorage.getItem('token') || sessionStorage.getItem('token') || '';
      const authHeaders = {
        Accept: 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };
      const apiBase = String(API_BASE || '').replace(/\/+$/, '');
      const fallbackBase = apiBase.replace(/\/api$/, '');
      const tenantId = getEffectiveTenantId();
      const tenantParam = tenantId ? `?tenant_id=${tenantId}` : '';

      const delUrls = [
        `${apiBase}/bulk/uploads/${uploadId}/failed-records${tenantParam}`,
        `${fallbackBase}/bulk/uploads/${uploadId}/failed-records${tenantParam}`,
        `${apiBase}/api/bulk/uploads/${uploadId}/failed-records${tenantParam}`,
      ];

      let res = null;
      for (const url of delUrls) {
        try {
          res = await axios.delete(url, { headers: authHeaders, timeout: 15000 });
          if (res?.data) break;
        } catch (_) { }
      }

      if (res?.data) {
        showTopToast('success', 'Failed Records Removed', res.data?.message || 'Failed records have been removed.');
        await fetchCsvJobDetails(uploadId);
        await fetchCsvStatusList(uploadId, true);
      } else {
        throw new Error('Could not remove failed records.');
      }
    } catch (err) {
      console.error('Failed to remove failed records:', err);
      showTopToast('error', 'Action Failed', err?.response?.data?.detail || err?.message || 'Unable to remove failed records.');
    }
  };

  const handleBulkCleanup = async (action) => {
    let title = '';
    let text = '';
    let confirmBtn = '';
    let btnColor = '#dc2626';

    if (action === 'complete_processing') {
      title = 'Complete All Processing Jobs?';
      text = 'This will mark all currently stuck/processing CSV uploads as Completed in the database.';
      confirmBtn = 'Yes, Mark All Completed';
      btnColor = '#15803d';
    } else if (action === 'cancel_stuck') {
      title = 'Pause / Cancel All Stuck Jobs?';
      text = 'This will pause all stuck/processing uploads so that you can resume them one by one whenever desired.';
      confirmBtn = 'Yes, Pause All Stuck Jobs';
      btnColor = '#d97706';
    } else if (action === 'delete_stuck') {
      title = 'Delete All Stuck Processing Jobs?';
      text = 'This will permanently DELETE all uploads currently in processing/queued state and purge local queue files.';
      confirmBtn = 'Yes, Delete All Stuck Jobs';
      btnColor = '#dc2626';
    } else if (action === 'delete_failed') {
      title = 'Delete All Failed Jobs?';
      text = 'This will permanently delete all uploads that are marked as failed or cancelled.';
      confirmBtn = 'Yes, Delete All Failed';
      btnColor = '#dc2626';
    } else if (action === 'clear_all_queues') {
      title = 'Clear Local Queue Files?';
      text = 'This will clean up pending local queue files from the filesystem queue.';
      confirmBtn = 'Yes, Clear Queue';
      btnColor = '#2563eb';
    } else if (action === 'recover_stale') {
      title = 'Recover Stuck Queue Files?';
      text = 'This will recover any unhandled or stalled queue files back into the active processing queue.';
      confirmBtn = 'Yes, Recover Queue Files';
      btnColor = '#2563eb';
    }

    try {
      const confirmResult = await showTopConfirm({
        title,
        text,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: confirmBtn,
        cancelButtonText: 'Cancel',
        confirmButtonColor: btnColor,
      });
      if (!confirmResult.isConfirmed) return;

      setCleaningQueue(true);
      const token = localStorage.getItem('token') || sessionStorage.getItem('token') || '';
      const authHeaders = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };
      const apiBase = String(API_BASE || '').replace(/\/+$/, '');
      const fallbackBase = apiBase.replace(/\/api$/, '');
      const tenantId = getEffectiveTenantId();

      const cleanupUrls = [
        `${apiBase}/bulk/uploads/cleanup`,
        `${fallbackBase}/bulk/uploads/cleanup`,
        `${apiBase}/api/bulk/uploads/cleanup`,
      ];

      let res = null;
      for (const url of cleanupUrls) {
        try {
          res = await axios.post(url, { action, tenant_id: tenantId }, { headers: authHeaders, timeout: 20000 });
          if (res?.data) break;
        } catch (_) { }
      }

      if (res?.data) {
        showTopToast('success', 'Operation Completed', res.data.message || 'Queue and upload data cleaned successfully.');
        setSelectedUploadId('');
        await fetchCsvStatusList(null, true);
      } else {
        throw new Error('Could not execute cleanup.');
      }
    } catch (err) {
      console.error('Failed to execute bulk cleanup:', err);
      showTopToast('error', 'Cleanup Failed', err?.response?.data?.detail || err?.message || 'Unable to execute cleanup.');
    } finally {
      setCleaningQueue(false);
    }
  };

  const handleRejectRecord = async (rec) => {
    if (!rec) return;
    const targetEmail = rec.email || rec.user_email || '';
    const targetType = rec.consent_type || rec.type || '';
    const consentRecordId = rec.consent_id || rec.consent_record_id || '';

    try {
      const confirmResult = await showTopConfirm({
        title: 'Reject / Revoke Consent?',
        html: `Are you sure you want to reject / revoke consent for <b>${targetEmail || 'Row #' + rec.row_number}</b>?<br/><span style="font-size:12px;color:#64748b;">This will revoke active consent in the database.</span>`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, Reject / Revoke',
        cancelButtonText: 'Cancel',
        confirmButtonColor: '#dc2626',
      });
      if (!confirmResult.isConfirmed) return;

      setRejectingRecordId(rec.id || rec.row_number);
      const token = localStorage.getItem('token') || sessionStorage.getItem('token') || '';
      const authHeaders = {
        Accept: 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };
      const tenantId = getEffectiveTenantId();

      const payload = {
        tenant_id: tenantId ?? '',
        full_name: rec.username || targetEmail || 'User',
        fullName: rec.username || targetEmail || 'User',
        email: targetEmail,
        request_type: 'erasure',
        requestType: 'erasure',
        request_details: `Revoke from CSV Upload Record row #${rec.row_number}. Consent ID: ${consentRecordId}. Consent Type: ${targetType}.`,
        requestDetails: `Revoke from CSV Upload Record row #${rec.row_number}. Consent ID: ${consentRecordId}. Consent Type: ${targetType}.`,
        request_reason: 'User / Manager rejected CSV record',
        requestReason: 'User / Manager rejected CSV record',
        personal_data_involved: 'email, consent',
        personalDataInvolved: 'email, consent',
        declaration_name: targetEmail || 'User',
        declarationName: targetEmail || 'User',
        declaration_date: new Date().toISOString().split('T')[0],
        declarationDate: new Date().toISOString().split('T')[0],
        send_email: 'false',
        source: 'csv_bulk_records_table',
      };

      await axios.post(`${API_BASE}/manager/dsar`, payload, { headers: authHeaders });

      showTopToast('success', 'Consent Revoked', `Consent for ${targetEmail || 'record'} has been rejected / revoked.`);

      if (selectedUploadId) {
        await fetchCsvJobDetails(selectedUploadId);
      }
    } catch (err) {
      console.error('Failed to revoke record:', err);
      showTopToast('error', 'Action Failed', err?.response?.data?.detail || err?.message || 'Could not reject / revoke this record.');
    } finally {
      setRejectingRecordId(null);
    }
  };

  const handleOpenCsvStatusModal = () => {
    setShowCsvStatusModal(true);
    fetchCsvStatusList();
  };

  useEffect(() => {
    if (!showCsvStatusModal) {
      if (pollingRef.current) clearInterval(pollingRef.current);
      return;
    }

    const currentStatus = String(selectedJobDetails?.live?.status || selectedJobDetails?.upload?.status || '').toLowerCase();
    const isTerminal = ['done', 'completed', 'finished', 'failed'].includes(currentStatus);

    if (!isTerminal && selectedUploadId) {
      pollingRef.current = setInterval(() => {
        fetchCsvJobDetails(selectedUploadId, null, 20);
      }, 5000);
    } else {
      if (pollingRef.current) clearInterval(pollingRef.current);
    }

    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [showCsvStatusModal, selectedUploadId, selectedJobDetails?.live?.status, selectedJobDetails?.upload?.status, fetchCsvJobDetails]);

  useEffect(() => {
    let mounted = true;
    const fetchData = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token') || sessionStorage.getItem('token') || '';
        let tenantId = userTenantId;
        if (tenantId === null && token) {
          try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            tenantId = payload.tenant_id || payload.tenant || payload.tid || null;
          } catch (_) { }
        }
        if (tenantId === null) {
          const storedTenant = localStorage.getItem('tenant_id') || sessionStorage.getItem('tenant_id');
          if (storedTenant) tenantId = Number(storedTenant) || null;
        }

        const params = { limit: 200 };
        if (tenantId !== null && tenantId !== undefined) params.tenant_id = tenantId;
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const response = await axios.get(`${API_BASE}/user/consent`, { params, headers, skipLoading: true });
        if (!mounted) return;
        const data = response.data;
        const consentRows = data && (data.consents || data.rows || Array.isArray(data))
          ? (data.consents || data.rows || data)
          : [];
        setRows(Array.isArray(consentRows) ? consentRows : []);
        setLoadError('');
      } catch (error) {
        if (!mounted) return;
        setRows([]);
        setLoadError(error?.response?.data?.detail || 'Unable to load consent records. Please try again.');
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetchData();
    return () => { mounted = false; };
  }, [userTenantId]);

  // ── Filtered + paginated ──────────────────────────────────────────────────
  const unameFilter = String(usernameFilter || '').toLowerCase().trim();
  const mailFilter = String(emailFilter || '').toLowerCase().trim();
  const filteredRows = rows.filter(r => {
    const uname = String(r.username || r.user_name || '').toLowerCase();
    const mail = String(r.email || r.responder_email || '').toLowerCase();
    return uname.includes(unameFilter) && mail.includes(mailFilter);
  });
  const totalItems = filteredRows.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const page = Math.min(Math.max(1, currentPage), totalPages);
  const start = (page - 1) * pageSize;
  const visibleRows = filteredRows.slice(start, start + pageSize);

  const openConsentHistory = async (record) => {
    if (!record?.id) return;
    setHistoryLoadingId(record.id);
    try {
      const response = await axios.get(`${API_BASE}/user/consent/${record.id}/activity`, {
        params: { limit: 100 },
      });
      const history = Array.isArray(response.data?.logs) ? response.data.logs : [];
      const encId = response.data?.encrypted_id || response.data?.public_id || record.public_id || (record.id ? `CNS-${String(record.id).padStart(8, '0')}` : '—');

      const escapeHtml = (value) => String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
      const describeAction = (action) => {
        const value = String(action || 'Activity').trim();
        if (value === 'email_user') return 'Email User (Notification)';
        if (value.toLowerCase() === 'current consent status') return 'Current Status';
        const match = value.match(/^(POST|PUT|PATCH|DELETE)\s+(.+)$/i);
        if (!match) return value;
        const [, method, path] = match;
        if (/\/consent\/revoke$/i.test(path)) return 'Consent Revoked';
        if (/\/consent\/\d+\/cancel$/i.test(path)) return 'Consent Cancelled';
        if (/\/consent\/\d+$/i.test(path) && method.toUpperCase() === 'PUT') return 'Consent Updated';
        if (/\/manager\/logs$/i.test(path)) return 'System Activity Log';
        if (/\/manager\/smtp-config$/i.test(path)) return 'SMTP Configuration';
        return `${method.toUpperCase()} ${path}`;
      };

      const renderActionBadge = (actionStr) => {
        const act = String(actionStr || '').trim();
        const lower = act.toLowerCase();
        if (lower.includes('active') || lower.includes('accepted') || lower.includes('grant')) {
          return `<span style="display:inline-flex;align-items:center;gap:4px;padding:3px 8px;background:#dcfce7;color:#15803d;border:1px solid #bbf7d0;border-radius:6px;font-size:12px;font-weight:600;white-space:nowrap;">✓ ${escapeHtml(act)}</span>`;
        }
        if (lower.includes('revok') || lower.includes('declin') || lower.includes('cancel') || lower.includes('reject')) {
          return `<span style="display:inline-flex;align-items:center;gap:4px;padding:3px 8px;background:#fee2e2;color:#b91c1c;border:1px solid #fecaca;border-radius:6px;font-size:12px;font-weight:600;white-space:nowrap;">✕ ${escapeHtml(act)}</span>`;
        }
        if (lower.includes('email') || lower.includes('notification')) {
          return `<span style="display:inline-flex;align-items:center;gap:4px;padding:3px 8px;background:#e0f2fe;color:#0369a1;border:1px solid #bae6fd;border-radius:6px;font-size:12px;font-weight:600;white-space:nowrap;">✉️ ${escapeHtml(act)}</span>`;
        }
        const methodMatch = act.match(/^(POST|GET|PUT|PATCH|DELETE)\s+(.*)$/i);
        if (methodMatch) {
          const [, method, path] = methodMatch;
          return `<span style="display:inline-block;padding:2px 6px;background:#f1f5f9;border:1px solid #cbd5e1;border-radius:4px;font-size:11px;font-weight:700;color:#0f172a;margin-right:4px;">${escapeHtml(method.toUpperCase())}</span><span style="font-family:monospace;font-size:12px;color:#334155;word-break:break-all;">${escapeHtml(path)}</span>`;
        }
        return `<span style="font-size:12.5px;font-weight:600;color:#1e293b;word-break:break-word;">${escapeHtml(act)}</span>`;
      };

      const historyRows = history.map((entry) => {
        const details = formatActivityDetails(entry.details);
        const action = entry.displayAction || describeAction(entry.action);
        const actor = entry.actor_email || (entry.action === 'Current consent status' ? 'Consent record' : '-');
        return `<tr>
          <td style="padding:12px 14px;border-bottom:1px solid #e2e8f0;white-space:nowrap;vertical-align:top;color:#475569;font-size:12.5px">${escapeHtml(formatIndianDateTime(entry.timestamp_ist || entry.timestamp))}</td>
          <td style="padding:12px 14px;border-bottom:1px solid #e2e8f0;vertical-align:top;line-height:1.4">${renderActionBadge(action)}</td>
          <td style="padding:12px 14px;border-bottom:1px solid #e2e8f0;vertical-align:top;color:#0f172a;font-size:12px;font-family:monospace;word-break:break-all;line-height:1.4">${escapeHtml(actor)}</td>
          <td style="padding:12px 14px;border-bottom:1px solid #e2e8f0;vertical-align:top;color:#475569;font-size:12px;line-height:1.5;word-break:break-word;overflow-wrap:anywhere">${escapeHtml(details)}</td>
        </tr>`;
      }).join('');

      const hasRecordedActivity = history.length > 0;
      const historyNote = hasRecordedActivity
        ? ''
        : `<div style="margin-bottom:12px;padding:10px 12px;background:#eff6ff;border:1px solid #bfdbfe;border-radius:6px;color:#1e40af;font-size:13px">
            No activity events have been recorded yet.
          </div>`;

      await Swal.fire({
        title: `Activity History: ${escapeHtml(record.email || record.username || 'Consent')} (Ref #${escapeHtml(encId)})`,
        html: `<div style="max-height:65vh;overflow-x:auto;overflow-y:auto;text-align:left;border:1px solid #e2e8f0;border-radius:8px">
          ${historyNote}
          <table style="width:100%;min-width:960px;border-collapse:collapse;font-size:13px;table-layout:fixed">
            <colgroup>
              <col style="width: 175px;" />
              <col style="width: 220px;" />
              <col style="width: 220px;" />
              <col style="width: auto;" />
            </colgroup>
            <thead><tr style="background:#f8fafc;position:sticky;top:0;z-index:2">
              <th style="padding:12px 14px;text-align:left;border-bottom:2px solid #cbd5e1;font-size:11.5px;font-weight:700;color:#475569;letter-spacing:0.5px;text-transform:uppercase;white-space:nowrap">When (IST)</th>
              <th style="padding:12px 14px;text-align:left;border-bottom:2px solid #cbd5e1;font-size:11.5px;font-weight:700;color:#475569;letter-spacing:0.5px;text-transform:uppercase;white-space:nowrap">What happened</th>
              <th style="padding:12px 14px;text-align:left;border-bottom:2px solid #cbd5e1;font-size:11.5px;font-weight:700;color:#475569;letter-spacing:0.5px;text-transform:uppercase;white-space:nowrap">Who</th>
              <th style="padding:12px 14px;text-align:left;border-bottom:2px solid #cbd5e1;font-size:11.5px;font-weight:700;color:#475569;letter-spacing:0.5px;text-transform:uppercase;min-width:320px">Details</th>
            </tr></thead>
            <tbody>${historyRows}</tbody>
          </table>
        </div>`,
        width: 1100,
        showCloseButton: true,
      });
    } catch (error) {
      await Swal.fire({
        icon: 'error',
        title: 'Unable to load activity history',
        text: error?.response?.data?.detail || 'Please try again.',
      });
    } finally {
      setHistoryLoadingId(null);
    }
  };

  // ── Back handler ──────────────────────────────────────────────────────────
  const handleBack = () => {
    try {
      // ✅ Option 1: agar onBack prop diya hua hai toh use karo
      if (typeof onBack === 'function') {
        onBack();
        return;
      }
      // ✅ Option 2: agar onMenu prop diya hua hai toh consent page pe jaao
      if (typeof onMenu === 'function') {
        onMenu('consent');
        // try { window.history.pushState({}, '', '/user/consent'); } catch (e) { }
        return;
      }
      // ✅ Option 3: fallback
      window.history.back();
      // setTimeout(() => {
      //   try { window.dispatchEvent(new PopStateEvent('popstate')); } catch (e) { }
      // }, 150);
    } catch (e) {
      window.location.href = '/user/consent';
    }
  };

  return (
    <div style={{ padding: 20, fontFamily: "'DM Sans', 'Segoe UI', system-ui, sans-serif" }}>

      {/* ── Header Row ── */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: 16,
        gap: 12,
      }}>

        {/* LEFT — Title + Filters + Count */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>

          <div style={{ fontSize: 22, fontWeight: 800, color: '#0f172a' }}>
            User Logs
          </div>

          {/* Filters */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              placeholder="Filter username"
              value={usernameFilter}
              onChange={(e) => { setUsernameFilter(e.target.value); setCurrentPage(1); }}
              style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff', fontSize: 13, outline: 'none' }}
            />
            <input
              placeholder="Filter email"
              value={emailFilter}
              onChange={(e) => { setEmailFilter(e.target.value); setCurrentPage(1); }}
              style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff', fontSize: 13, outline: 'none' }}
            />
            {(usernameFilter || emailFilter) && (
              <button
                onClick={() => { setUsernameFilter(''); setEmailFilter(''); setCurrentPage(1); }}
                style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer', fontSize: 13 }}
              >
                Clear
              </button>
            )}
          </div>

          {/* Count */}
          <div style={{ fontSize: 13, color: '#475569' }}>
            Showing {totalItems === 0 ? 0 : Math.min(start + 1, totalItems)} – {Math.min(start + pageSize, totalItems)} of {totalItems}
          </div>
        </div>

        {/* RIGHT — Back Button & CSV Status Button */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0, marginTop: 4 }}>
          {/* Back Button */}
          <button
            onClick={handleBack}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '8px 18px', borderRadius: 8,
              border: '1.5px solid #e2e8f0', background: '#f8fafc',
              color: '#475569', fontSize: 14, fontWeight: 600,
              cursor: 'pointer', flexShrink: 0,
              transition: 'all 0.15s',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.background = '#f1f5f9';
              e.currentTarget.style.borderColor = '#cbd5e1';
              e.currentTarget.style.color = '#1e293b';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = '#f8fafc';
              e.currentTarget.style.borderColor = '#e2e8f0';
              e.currentTarget.style.color = '#475569';
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2"
              strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 5l-7 7 7 7" />
            </svg>
            Back
          </button>

          {/* CSV Status Button */}
          <button
            onClick={handleOpenCsvStatusModal}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 7,
              padding: '8px 18px', borderRadius: 8,
              border: '1.5px solid #2563eb',
              background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
              color: '#ffffff', fontSize: 14, fontWeight: 600,
              cursor: 'pointer', flexShrink: 0,
              boxShadow: '0 2px 4px rgba(37, 99, 235, 0.25)',
              transition: 'all 0.15s ease-in-out',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.background = 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)';
              e.currentTarget.style.boxShadow = '0 4px 8px rgba(37, 99, 235, 0.35)';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)';
              e.currentTarget.style.boxShadow = '0 2px 4px rgba(37, 99, 235, 0.25)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
            title="CSV Upload & Email Sending Status"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2"
              strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="16" y1="17" x2="8" y2="17" />
              <polyline points="10 9 9 9 8 9" />
            </svg>
            <span>CSV Status</span>
          </button>
        </div>

      </div>
      {/* ── End Header ── */}

      <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #eef2f7', overflow: 'hidden' }}>
        {loadError && (
          <div role="alert" style={{ padding: '12px 16px', color: '#9a3412', background: '#fff7ed', borderBottom: '1px solid #fed7aa' }}>
            {loadError}
          </div>
        )}
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Loading…</div>
        ) : (
          <div>

            <div style={{ padding: '20px 16px 8px' }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a' }}>Consent Records</div>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
                Current consent status and response details.
              </div>
            </div>

            {/* Per page control */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', padding: '10px 16px', gap: 10 }}>
              <label style={{ fontSize: 13, color: '#475569' }}>Per page:</label>
              <select
                value={pageSize}
                onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
                style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff' }}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>

            {/* Table */}
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', minWidth: 1200, borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                    {['#', 'Username', 'Email', 'Created At', 'Responded At', 'Consent Type', 'Status', 'Browser', 'IP Address', 'Expiry Count', 'Last Logs History'].map(h => (
                      <th key={h} style={{
                        padding: '10px 14px', textAlign: 'left',
                        fontSize: 11.5, fontWeight: 700, color: '#64748b',
                        letterSpacing: 0.5, textTransform: 'uppercase', whiteSpace: 'nowrap',
                      }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.length === 0 ? (
                    <tr>
                      <td colSpan={11} style={{ padding: 40, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
                        No logs found.
                      </td>
                    </tr>
                  ) : (
                    visibleRows.map((r, i) => {
                      const statusVal = r.status || r.user_action || r.response || '';
                      const browserRaw = r.browser || r.user_agent || '';
                      return (
                        <tr
                          key={r.id || i}
                          style={{ borderBottom: '1px solid #f1f5f9', transition: 'background 0.12s' }}
                          onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                        >
                          {/* Row number */}
                          <td style={{ padding: '10px 14px', fontSize: 12, color: '#94a3b8', textAlign: 'center' }}>
                            {start + i + 1}
                          </td>

                          {/* Username */}
                          <td style={{ padding: '10px 14px', fontSize: 13, fontWeight: 600, color: '#1e293b' }}>
                            {r.username || r.user_name || '-'}
                          </td>

                          {/* Email */}
                          <td style={{ padding: '10px 14px', fontSize: 13, color: '#475569' }}>
                            {maskEmail(r.email || r.responder_email) || '-'}
                          </td>

                          {/* Created At */}
                          <td style={{ padding: '10px 14px', fontSize: 13, color: '#475569' }}>
                            {formatIndianDateTime(r.created_at || r.createdAt || r.timestamp || r.created)}
                          </td>

                          {/* Responded At */}
                          <td style={{ padding: '10px 14px', fontSize: 13, color: '#475569' }}>
                            {formatIndianDateTime(r.user_responded_at || r.email_response_at || r.response_at || r.user_action_at || r.updated_at || r.last_email_sent_at)}
                          </td>

                          {/* Consent Type */}
                          <td style={{ padding: '10px 14px', fontSize: 13, color: '#475569' }}>
                            {r.type || r.consent_type || '-'}
                          </td>

                          {/* Status */}
                          <td style={{ padding: '10px 14px' }}>
                            <EmailStatusIcon status={statusVal} />
                          </td>

                          {/* Browser */}
                          <td style={{ padding: '10px 14px' }}>
                            <BrowserCell browserRaw={browserRaw} />
                          </td>

                          {/* IP */}
                          <td style={{ padding: '10px 14px', fontSize: 13, color: '#475569', fontFamily: 'monospace' }}>
                            {r.ip_address || r.ip || r.client_ip || '-'}
                          </td>

                          {/* Expiry Count */}
                          <td style={{ padding: '10px 14px', fontSize: 13, color: '#475569', textAlign: 'center' }}>
                            {r.reminder_count !== undefined && r.reminder_count !== null
                              ? r.reminder_count
                              : (r.expiry || 0)}
                          </td>

                          {/* Per-record activity history */}
                          <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                            <button
                              type="button"
                              onClick={() => openConsentHistory(r)}
                              disabled={historyLoadingId === r.id}
                              style={{
                                padding: '6px 10px',
                                border: '1px solid #bfdbfe',
                                borderRadius: 6,
                                background: historyLoadingId === r.id ? '#f1f5f9' : '#eff6ff',
                                color: '#1d4ed8',
                                fontSize: 12,
                                fontWeight: 600,
                                cursor: historyLoadingId === r.id ? 'wait' : 'pointer',
                              }}
                            >
                              {historyLoadingId === r.id ? 'Loading…' : 'View details'}
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', padding: '12px 16px', gap: 8 }}>
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage <= 1}
                style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff', cursor: currentPage <= 1 ? 'not-allowed' : 'pointer', opacity: currentPage <= 1 ? 0.5 : 1 }}
              >
                Prev
              </button>
              <span style={{ fontSize: 13, color: '#475569', padding: '0 8px' }}>
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage >= totalPages}
                style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff', cursor: currentPage >= totalPages ? 'not-allowed' : 'pointer', opacity: currentPage >= totalPages ? 0.5 : 1 }}
              >
                Next
              </button>
            </div>

          </div>
        )}
      </div>

      {/* ── CSV Status Modal ── */}
      {showCsvStatusModal && (() => {
        const upload = selectedJobDetails?.upload || {};
        const live = selectedJobDetails?.live || {};
        const emailJobs = upload.email_jobs || {};

        const rawStatus = String(live.status || upload.status || 'unknown').toLowerCase();
        const isCompleted = ['done', 'completed', 'finished'].includes(rawStatus);
        const isPartial = rawStatus === 'partial';
        const isFailed = rawStatus === 'failed';
        const isTerminal = isCompleted || isPartial || isFailed;
        const isProcessing = ['processing', 'queued', 'uploading'].includes(rawStatus);
        const isPaused = ['cancelled', 'paused'].includes(rawStatus);

        // CSV metrics - always prioritize upload.total_rows (the ground-truth file count)
        const csvTotal = Number(upload.total_rows || live.total_records || upload.total || 0);
        const csvProcessed = Number(live.processed_rows || upload.processed_rows || 0);
        const csvSaved = Number(live.inserted !== undefined ? live.inserted : (upload.inserted_rows || upload.successful_rows || 0));
        const csvFailed = Number(live.failed_rows !== undefined ? live.failed_rows : (live.failed !== undefined ? live.failed : (upload.failed_rows || 0)));
        const allRecords = selectedJobDetails?.records || [];
        const detectedDuplicates = allRecords.filter(r => (r.error_message || '').toLowerCase().includes('duplicate') || r.status === 'skipped').length;
        const csvDuplicates = Number(live.duplicates !== undefined ? live.duplicates : (upload.duplicate_rows !== undefined ? upload.duplicate_rows : detectedDuplicates));
        let csvPending = 0;
        if (csvTotal > 0) {
          csvPending = Math.max(0, csvTotal - (csvSaved + csvFailed));
        }

        let csvPercent = 0;
        if (csvTotal > 0) {
          if (isCompleted) {
            csvPercent = 100;
          } else {
            const activeCount = Math.max(csvProcessed, csvSaved + csvFailed);
            csvPercent = Math.min(100, Math.max(0, Math.round((activeCount / csvTotal) * 100)));
          }
        } else if (isTerminal && !isFailed) {
          csvPercent = 100;
        } else if (live.progress_percent !== undefined && live.progress_percent !== null && !isNaN(Number(live.progress_percent))) {
          csvPercent = Math.min(100, Math.max(0, Number(live.progress_percent)));
        }
        csvPercent = Math.min(100, Math.max(0, csvPercent));

        const filteredRecords = allRecords.filter(r => {
          const s = csvRecordSearch.toLowerCase().trim();
          const matchesSearch = !s || (r.email || '').toLowerCase().includes(s) || (r.username || '').toLowerCase().includes(s) || String(r.row_number || '').includes(s);
          if (!matchesSearch) return false;
          if (csvRecordStatusFilter === 'all') return true;
          if (csvRecordStatusFilter === 'completed') return r.status === 'completed';
          if (csvRecordStatusFilter === 'failed') return r.status === 'failed';
          if (csvRecordStatusFilter === 'duplicate') return (r.error_message || '').toLowerCase().includes('duplicate') || r.status === 'skipped';
          return true;
        });

        // Email metrics
        const emailTotal = Number(emailJobs.total || 0);
        const emailDelivered = Number(emailJobs.delivered || 0);
        const emailAccepted = Number(emailJobs.accepted || 0);
        const emailSent = emailDelivered + emailAccepted;
        const emailFailed = Number((emailJobs.failed || 0) + (emailJobs.bounced || 0));
        const emailQueued = Number(emailJobs.queued || 0);
        let emailPending = 0;
        if (emailTotal > 0) {
          emailPending = Math.max(0, emailTotal - (emailSent + emailFailed));
        } else {
          emailPending = emailQueued;
        }

        let emailPercent = 0;
        if (emailTotal > 0) {
          if (isCompleted && emailPending === 0) {
            emailPercent = 100;
          } else {
            emailPercent = Math.min(100, Math.max(0, Math.round(((emailSent + emailFailed) / emailTotal) * 100)));
          }
        } else if (isTerminal) {
          emailPercent = 100;
        }

        // Format dates
        const createdAtFormatted = upload.created_at ? new Date(upload.created_at).toLocaleString() : 'Recent';
        const completedAtFormatted = upload.completed_at ? new Date(upload.completed_at).toLocaleString() : null;

        return (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 12000,
              background: 'rgba(15, 23, 42, 0.65)',
              backdropFilter: 'blur(4px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 16,
              boxSizing: 'border-box',
            }}
            onClick={(e) => {
              if (e.target === e.currentTarget) setShowCsvStatusModal(false);
            }}
          >
            <div
              style={{
                width: '100%',
                maxWidth: 760,
                maxHeight: '92vh',
                display: 'flex',
                flexDirection: 'column',
                background: '#ffffff',
                borderRadius: 14,
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                border: '1px solid #e2e8f0',
                overflow: 'hidden',
              }}
            >
              {/* Modal Header */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '16px 22px',
                  background: '#f8fafc',
                  borderBottom: '1px solid #e2e8f0',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div
                    style={{
                      width: 38,
                      height: 38,
                      borderRadius: 10,
                      background: 'linear-gradient(135deg, #2563eb, #1d4ed8)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#ffffff',
                      boxShadow: '0 2px 6px rgba(37, 99, 235, 0.3)',
                    }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                      <line x1="16" y1="13" x2="8" y2="13" />
                      <line x1="16" y1="17" x2="8" y2="17" />
                    </svg>
                  </div>
                  <div>
                    <div style={{ fontSize: 17, fontWeight: 700, color: '#0f172a' }}>
                      CSV Upload & Email Status
                    </div>
                    <div style={{ fontSize: 12, color: '#64748b' }}>
                      Track database storage and email delivery progress in real-time
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 8, position: 'relative' }}>
                  {/* Queue Management Dropdown */}
                  <div style={{ position: 'relative' }}>
                    {/* <button
                      onClick={() => setShowCleanupMenu(prev => !prev)}
                      disabled={cleaningQueue}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 5,
                        padding: '6px 12px',
                        borderRadius: 6,
                        border: '1px solid #cbd5e1',
                        background: showCleanupMenu ? '#f1f5f9' : '#ffffff',
                        color: '#0f172a',
                        fontSize: 12,
                        fontWeight: 600,
                        cursor: cleaningQueue ? 'not-allowed' : 'pointer',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                      }}
                      title="Queue Management & Bulk Actions"
                    >
                      <span style={{ fontSize: 13 }}>⚙️</span>
                      {cleaningQueue ? 'Cleaning...' : 'Queue Actions ▾'}
                    </button> */}

                    {showCleanupMenu && (
                      <div
                        style={{
                          position: 'absolute',
                          right: 0,
                          top: '100%',
                          marginTop: 6,
                          width: 270,
                          background: '#ffffff',
                          borderRadius: 10,
                          boxShadow: '0 12px 28px -4px rgba(0,0,0,0.18), 0 8px 12px -6px rgba(0,0,0,0.1)',
                          border: '1px solid #cbd5e1',
                          padding: '6px 0',
                          zIndex: 100,
                          display: 'flex',
                          flexDirection: 'column',
                        }}
                      >
                        <div style={{ padding: '6px 14px', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                          Queue & Worker Actions
                        </div>

                        <button
                          onClick={() => { setShowCleanupMenu(false); handleBulkCleanup('cancel_stuck'); }}
                          style={{
                            padding: '9px 14px',
                            textAlign: 'left',
                            background: 'transparent',
                            border: 'none',
                            fontSize: 12.5,
                            color: '#b45309',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = '#fffbeb'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                        >
                          <span style={{ fontSize: 16 }}>⏸️</span>
                          <div>
                            <div style={{ fontWeight: 700 }}>Pause All Active Uploads</div>
                            <div style={{ fontSize: 11, color: '#92400e' }}>Stops parallel load so you can run 1 by 1</div>
                          </div>
                        </button>

                        <button
                          onClick={() => { setShowCleanupMenu(false); handleBulkCleanup('clear_all_queues'); }}
                          style={{
                            padding: '9px 14px',
                            textAlign: 'left',
                            background: 'transparent',
                            border: 'none',
                            fontSize: 12.5,
                            color: '#1d4ed8',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = '#eff6ff'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                        >
                          <span style={{ fontSize: 16 }}>🧹</span>
                          <div>
                            <div style={{ fontWeight: 700 }}>Clear Stuck Queue Files</div>
                            <div style={{ fontSize: 11, color: '#64748b' }}>Deletes orphan queue files in storage</div>
                          </div>
                        </button>

                        <button
                          onClick={() => { setShowCleanupMenu(false); handleBulkCleanup('recover_stale'); }}
                          style={{
                            padding: '9px 14px',
                            textAlign: 'left',
                            background: 'transparent',
                            border: 'none',
                            fontSize: 12.5,
                            color: '#0284c7',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = '#f0f9ff'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                        >
                          <span style={{ fontSize: 16 }}>🔄</span>
                          <div>
                            <div style={{ fontWeight: 700 }}>Recover Stuck Jobs</div>
                            <div style={{ fontSize: 11, color: '#64748b' }}>Unfreezes stalled processing jobs</div>
                          </div>
                        </button>

                        <div style={{ height: 1, background: '#f1f5f9', margin: '4px 0' }} />

                        <button
                          onClick={() => { setShowCleanupMenu(false); handleBulkCleanup('complete_processing'); }}
                          style={{
                            padding: '9px 14px',
                            textAlign: 'left',
                            background: 'transparent',
                            border: 'none',
                            fontSize: 12.5,
                            color: '#166534',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = '#f0fdf4'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                        >
                          <span style={{ fontSize: 16 }}>✅</span>
                          <div>
                            <div style={{ fontWeight: 700 }}>Mark All Stuck Completed</div>
                            <div style={{ fontSize: 11, color: '#64748b' }}>If rows are already in database</div>
                          </div>
                        </button>

                        <button
                          onClick={() => { setShowCleanupMenu(false); handleBulkCleanup('delete_stuck'); }}
                          style={{
                            padding: '9px 14px',
                            textAlign: 'left',
                            background: 'transparent',
                            border: 'none',
                            fontSize: 12.5,
                            color: '#dc2626',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = '#fef2f2'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                        >
                          <span style={{ fontSize: 16 }}>🗑️</span>
                          <div>
                            <div style={{ fontWeight: 700 }}>Delete All Stuck Jobs</div>
                            <div style={{ fontSize: 11, color: '#b91c1c' }}>Purges stuck jobs & queue records</div>
                          </div>
                        </button>

                        <button
                          onClick={() => { setShowCleanupMenu(false); handleBulkCleanup('delete_failed'); }}
                          style={{
                            padding: '9px 14px',
                            textAlign: 'left',
                            background: 'transparent',
                            border: 'none',
                            fontSize: 12.5,
                            color: '#dc2626',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = '#fef2f2'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                        >
                          <span style={{ fontSize: 16 }}>🗑️</span>
                          <div>
                            <div style={{ fontWeight: 700 }}>Delete All Failed Jobs</div>
                            <div style={{ fontSize: 11, color: '#b91c1c' }}>Removes failed & cancelled uploads</div>
                          </div>
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Refresh Button */}
                  <button
                    onClick={() => fetchCsvStatusList(selectedUploadId, true)}
                    disabled={csvStatusLoading || csvStatusRefreshing}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 5,
                      padding: '6px 12px',
                      borderRadius: 6,
                      border: '1px solid #cbd5e1',
                      background: '#ffffff',
                      color: '#334155',
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: (csvStatusLoading || csvStatusRefreshing) ? 'not-allowed' : 'pointer',
                    }}
                    title="Refresh Status"
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.2"
                    >
                      <path d="M23 4v6h-6M1 20v-6h6" />
                      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
                    </svg>
                    Refresh
                  </button>

                  {/* Close Button */}
                  <button
                    onClick={() => setShowCsvStatusModal(false)}
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: 6,
                      border: 'none',
                      background: 'transparent',
                      color: '#64748b',
                      fontSize: 18,
                      fontWeight: 700,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = '#e2e8f0'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                    title="Close"
                  >
                    ✕
                  </button>
                </div>
              </div>

              {/* Modal Body */}
              <div style={{ padding: '20px 24px', overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 16 }}>
                {csvStatusError && (
                  <div
                    style={{
                      padding: '10px 14px',
                      borderRadius: 8,
                      background: '#fef2f2',
                      border: '1px solid #fecaca',
                      color: '#991b1b',
                      fontSize: 13,
                    }}
                  >
                    {csvStatusError}
                  </div>
                )}

                {/* Multi-job Notice Banner if 2+ jobs are active */}
                {csvJobs.filter(j => ['processing', 'queued', 'uploading'].includes(String(j.status || '').toLowerCase())).length > 1 && (
                  <div
                    style={{
                      background: '#fffbeb',
                      border: '1.5px solid #fde68a',
                      borderRadius: 10,
                      padding: '12px 16px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      flexWrap: 'wrap',
                      gap: 12,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, flex: 1, minWidth: 260 }}>
                      <span style={{ fontSize: 20, lineHeight: 1 }}>⚡</span>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: '#92400e' }}>
                          {csvJobs.filter(j => ['processing', 'queued', 'uploading'].includes(String(j.status || '').toLowerCase())).length} Large Files Currently in Active Queue
                        </div>
                        {/* <div style={{ fontSize: 12, color: '#b45309', marginTop: 3, lineHeight: 1.5 }}>
                          Bahut saare large CSV files (jaise 10 files) ek sath chalane se system aur database lock ho sakta hai. <b>"Pause All"</b> karein aur fir <b>1 by 1</b> (ya 2-2 karke) <b>"Resume"</b> karein — isse processing fast aur smooth complete hogi!
                        </div> */}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <button
                        onClick={() => handleBulkCleanup('cancel_stuck')}
                        disabled={cleaningQueue}
                        style={{
                          padding: '7px 14px',
                          borderRadius: 6,
                          background: '#f59e0b',
                          color: '#ffffff',
                          border: 'none',
                          fontWeight: 700,
                          fontSize: 12,
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                          boxShadow: '0 1px 3px rgba(245,158,11,0.3)',
                        }}
                      >
                        ⏸️ Pause All Stuck Jobs
                      </button>
                      <button
                        onClick={() => handleBulkCleanup('clear_all_queues')}
                        disabled={cleaningQueue}
                        style={{
                          padding: '7px 12px',
                          borderRadius: 6,
                          background: '#ffffff',
                          color: '#b45309',
                          border: '1px solid #fde68a',
                          fontWeight: 600,
                          fontSize: 12,
                          cursor: 'pointer',
                        }}
                      >
                        🧹 Clear Queue
                      </button>
                    </div>
                  </div>
                )}

                {/* Job Selector Dropdown & Action Controls */}
                {csvJobs.length > 0 && (
                  <div
                    style={{
                      background: '#f8fafc',
                      padding: '12px 16px',
                      borderRadius: 10,
                      border: '1px solid #e2e8f0',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 12,
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 12,
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 260 }}>
                        <span style={{ fontSize: 13, fontWeight: 700, color: '#334155', whiteSpace: 'nowrap' }}>
                          Selected CSV:
                        </span>
                        <select
                          value={selectedUploadId}
                          onChange={(e) => {
                            const newUploadId = e.target.value;
                            setSelectedUploadId(newUploadId);
                            fetchCsvJobDetails(newUploadId);
                          }}
                          style={{
                            flex: 1,
                            minWidth: 0,
                            padding: '7px 12px',
                            borderRadius: 6,
                            border: '1px solid #cbd5e1',
                            background: '#ffffff',
                            fontSize: 13,
                            fontWeight: 500,
                            color: '#0f172a',
                            outline: 'none',
                          }}
                        >
                          {csvJobs.map((job) => (
                            <option key={job.upload_id} value={job.upload_id}>
                              {job.filename || 'Upload'} — {job.total_rows || 0} rows ({job.status || 'queued'})
                            </option>
                          ))}
                        </select>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                        <span
                          style={{
                            fontSize: 11,
                            fontWeight: 700,
                            padding: '3px 10px',
                            borderRadius: 12,
                            textTransform: 'uppercase',
                            background: isCompleted ? '#dcfce7' : isFailed ? '#fee2e2' : isPaused ? '#fef3c7' : '#dbeafe',
                            color: isCompleted ? '#15803d' : isFailed ? '#b91c1c' : isPaused ? '#92400e' : '#1d4ed8',
                          }}
                        >
                          {rawStatus || 'Queued'}
                        </span>
                        <span style={{ fontSize: 12, color: '#64748b' }}>
                          Uploaded: {createdAtFormatted}
                        </span>
                      </div>
                    </div>

                    {/* Dedicated Action Buttons for Selected CSV */}
                    <div
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        borderTop: '1px solid #e2e8f0',
                        paddingTop: 10,
                        gap: 8,
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        {/* Resume / Start / Restart Button */}
                        <button
                          onClick={() => handleResumeUpload(selectedUploadId)}
                          disabled={resumingUploadId === selectedUploadId || csvStatusLoading}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                            padding: '6px 14px',
                            borderRadius: 6,
                            border: '1px solid #2563eb',
                            background: '#2563eb',
                            color: '#ffffff',
                            fontSize: 12,
                            fontWeight: 600,
                            cursor: (resumingUploadId === selectedUploadId || csvStatusLoading) ? 'not-allowed' : 'pointer',
                            boxShadow: '0 1px 2px rgba(37,99,235,0.2)',
                          }}
                          title="Resume or restart processing this file from the last checkpoint"
                        >
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
                            <path d="M23 4v6h-6M1 20v-6h6" />
                            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
                          </svg>
                          {resumingUploadId === selectedUploadId
                            ? 'Starting...'
                            : isProcessing
                              ? 'Restart / Re-queue'
                              : isPaused
                                ? '▶ Resume Processing'
                                : isFailed
                                  ? 'Retry Failed Rows'
                                  : isCompleted
                                    ? 'Re-process CSV'
                                    : 'Start / Resume'}
                        </button>

                        {/* Pause Button (if currently processing or queued) */}
                        {(isProcessing || rawStatus === 'queued' || rawStatus === 'uploading') && (
                          <button
                            onClick={() => handleCancelUpload(selectedUploadId)}
                            disabled={cancellingUploadId === selectedUploadId || csvStatusLoading}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 6,
                              padding: '6px 14px',
                              borderRadius: 6,
                              border: '1px solid #d97706',
                              background: '#fffbeb',
                              color: '#b45309',
                              fontSize: 12,
                              fontWeight: 600,
                              cursor: (cancellingUploadId === selectedUploadId || csvStatusLoading) ? 'not-allowed' : 'pointer',
                            }}
                            title="Pause this upload so you can process another file first"
                          >
                            ⏸️ {cancellingUploadId === selectedUploadId ? 'Pausing...' : 'Pause Upload'}
                          </button>
                        )}

                        {/* Delete Upload */}
                        <button
                          onClick={() => handleDeleteUpload(selectedUploadId)}
                          disabled={deletingUploadId === selectedUploadId || csvStatusLoading}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 5,
                            padding: '6px 12px',
                            borderRadius: 6,
                            border: '1px solid #fecaca',
                            background: '#ffffff',
                            color: '#dc2626',
                            fontSize: 12,
                            fontWeight: 600,
                            cursor: (deletingUploadId === selectedUploadId || csvStatusLoading) ? 'not-allowed' : 'pointer',
                          }}
                          title="Permanently remove this upload entry from the list"
                        >
                          🗑️ {deletingUploadId === selectedUploadId ? 'Deleting...' : 'Delete'}
                        </button>
                      </div>

                      <div style={{ fontSize: 11.5, color: '#64748b' }}>
                        ID: <code style={{ color: '#0f172a' }}>{selectedUploadId ? selectedUploadId.slice(0, 14) + '...' : '—'}</code>
                      </div>
                    </div>
                  </div>
                )}

                {/* Empty State */}
                {csvJobs.length === 0 && !csvStatusLoading && (
                  <div style={{ textAlign: 'center', padding: '36px 16px', color: '#64748b' }}>
                    <div style={{ fontSize: 32, marginBottom: 8 }}>📄</div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: '#1e293b' }}>
                      No Bulk CSV Upload Jobs Found
                    </div>
                    <div style={{ fontSize: 13, marginTop: 4, maxWidth: 440, margin: '6px auto 0' }}>
                      Once you upload a CSV file in the User Panel, its database saving status and email sending percentage will appear here.
                    </div>
                  </div>
                )}

                {/* Loading Indicator */}
                {csvStatusLoading && (
                  <div style={{ textAlign: 'center', padding: '30px 16px', color: '#2563eb' }}>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>Loading CSV & Email status...</div>
                  </div>
                )}

                {/* Detailed Status Sections */}
                {csvJobs.length > 0 && !csvStatusLoading && (
                  <>
                    {/* ── COMPLETE STATUS SUMMARY CALLOUT ── */}
                    {isTerminal ? (
                      <div
                        style={{
                          background: isFailed ? '#fff5f5' : isPartial ? '#fffbeb' : '#f0fdf4',
                          border: `1.5px solid ${isFailed ? '#fca5a5' : isPartial ? '#fcd34d' : '#86efac'}`,
                          borderRadius: 10,
                          padding: '14px 18px',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 10,
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 6 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontSize: 18 }}>{isFailed ? '⚠️' : isPartial ? '⚡' : '✅'}</span>
                            <span
                              style={{
                                fontSize: 14,
                                fontWeight: 700,
                                color: isFailed ? '#991b1b' : isPartial ? '#92400e' : '#166534',
                              }}
                            >
                              {isFailed
                                ? 'Upload Processing Failed'
                                : isPartial
                                  ? 'Upload Processing Partially Completed'
                                  : 'Upload & Email Delivery Completed'}
                            </span>
                          </div>
                          {completedAtFormatted && (
                            <span style={{ fontSize: 12, color: '#64748b' }}>
                              Finished at: {completedAtFormatted}
                            </span>
                          )}
                        </div>

                        {/* Breakdown Grid */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                          {/* CSV Summary */}
                          <div
                            style={{
                              background: '#ffffff',
                              borderRadius: 8,
                              padding: '10px 14px',
                              border: '1px solid #e2e8f0',
                            }}
                          >
                            <div style={{ fontSize: 12, fontWeight: 700, color: '#1e293b', marginBottom: 4 }}>
                              CSV File Upload & Database:
                            </div>
                            <div style={{ fontSize: 12, color: '#334155', lineHeight: 1.6 }}>
                              • <b>{csvSaved.toLocaleString()}</b> Saved in Database<br />
                              • <b style={{ color: csvFailed > 0 ? '#b91c1c' : '#334155' }}>{csvFailed.toLocaleString()}</b> Failed<br />
                              • <b>{csvPending.toLocaleString()}</b> Pending<br />
                              • <b>{csvTotal.toLocaleString()}</b> Total Rows
                            </div>
                          </div>

                          {/* Email Summary */}
                          <div
                            style={{
                              background: '#ffffff',
                              borderRadius: 8,
                              padding: '10px 14px',
                              border: '1px solid #e2e8f0',
                            }}
                          >
                            <div style={{ fontSize: 12, fontWeight: 700, color: '#1e293b', marginBottom: 4 }}>
                              Email Notification Delivery:
                            </div>
                            <div style={{ fontSize: 12, color: '#334155', lineHeight: 1.6 }}>
                              • <b>{emailSent.toLocaleString()}</b> Sent / Delivered<br />
                              • <b style={{ color: emailFailed > 0 ? '#b91c1c' : '#334155' }}>{emailFailed.toLocaleString()}</b> Failed<br />
                              • <b>{emailPending.toLocaleString()}</b> Pending<br />
                              • <b>{emailTotal.toLocaleString()}</b> Total Emails
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div
                        style={{
                          background: '#eff6ff',
                          border: '1.5px solid #bfdbfe',
                          borderRadius: 10,
                          padding: '12px 16px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                        }}
                      >
                        <div
                          style={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            background: '#2563eb',
                            boxShadow: '0 0 0 4px rgba(37, 99, 235, 0.2)',
                          }}
                        />
                        <div style={{ fontSize: 13, color: '#1e40af', fontWeight: 600 }}>
                          Currently processing CSV upload & email delivery in background (Auto-refreshing every 3s)...
                        </div>
                      </div>
                    )}

                    {/* ── CASE 1: CSV FILE UPLOAD STATUS ── */}
                    <div
                      style={{
                        background: '#ffffff',
                        border: '1px solid #e2e8f0',
                        borderRadius: 12,
                        padding: '18px 20px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontSize: 16 }}>📊</span>
                          <div>
                            <span style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>
                              CSV File Upload & Database Save
                            </span>
                            <span style={{ fontSize: 12, color: '#64748b', marginLeft: 8 }}>
                              ({csvSaved.toLocaleString()} of {csvTotal.toLocaleString()} rows saved)
                            </span>
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span
                            style={{
                              fontSize: 18,
                              fontWeight: 800,
                              color: isCompleted ? '#16a34a' : isFailed ? '#dc2626' : '#2563eb',
                            }}
                          >
                            {csvPercent}%
                          </span>
                        </div>
                      </div>

                      {/* Progress Bar */}
                      <div
                        style={{
                          width: '100%',
                          height: 12,
                          background: '#e2e8f0',
                          borderRadius: 6,
                          overflow: 'hidden',
                          marginBottom: 16,
                          position: 'relative',
                        }}
                      >
                        <div
                          style={{
                            width: `${csvPercent}%`,
                            height: '100%',
                            background: isCompleted
                              ? 'linear-gradient(90deg, #10b981 0%, #059669 100%)'
                              : isFailed
                                ? 'linear-gradient(90deg, #ef4444 0%, #dc2626 100%)'
                                : 'linear-gradient(90deg, #3b82f6 0%, #2563eb 100%)',
                            borderRadius: 6,
                            transition: 'width 0.4s ease-in-out',
                          }}
                        />
                      </div>

                      {/* 5 Cards: Total, Saved in DB, Failed, Duplicates, Pending */}
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8 }}>
                        {/* Total Rows */}
                        <div
                          style={{
                            background: '#f8fafc',
                            border: '1px solid #e2e8f0',
                            borderRadius: 8,
                            padding: '10px 8px',
                            textAlign: 'center',
                          }}
                        >
                          <div style={{ fontSize: 10.5, color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>
                            Total Rows
                          </div>
                          <div style={{ fontSize: 18, fontWeight: 800, color: '#0f172a', marginTop: 2 }}>
                            {csvTotal.toLocaleString()}
                          </div>
                          <div style={{ fontSize: 10.5, color: '#94a3b8', marginTop: 1 }}>In file</div>
                        </div>

                        {/* Database Saved */}
                        <div
                          style={{
                            background: '#ecfdf5',
                            border: '1px solid #a7f3d0',
                            borderRadius: 8,
                            padding: '10px 8px',
                            textAlign: 'center',
                          }}
                        >
                          <div style={{ fontSize: 10.5, color: '#065f46', fontWeight: 600, textTransform: 'uppercase' }}>
                            Saved in DB
                          </div>
                          <div style={{ fontSize: 18, fontWeight: 800, color: '#047857', marginTop: 2 }}>
                            {csvSaved.toLocaleString()}
                          </div>
                          <div style={{ fontSize: 10.5, color: '#10b981', marginTop: 1 }}>Saved in DB</div>
                        </div>

                        {/* Failed Rows */}
                        <div
                          style={{
                            background: csvFailed > 0 ? '#fef2f2' : '#f8fafc',
                            border: `1px solid ${csvFailed > 0 ? '#fecaca' : '#e2e8f0'}`,
                            borderRadius: 8,
                            padding: '10px 8px',
                            textAlign: 'center',
                          }}
                        >
                          <div style={{ fontSize: 10.5, color: csvFailed > 0 ? '#991b1b' : '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>
                            Failed Rows
                          </div>
                          <div style={{ fontSize: 18, fontWeight: 800, color: csvFailed > 0 ? '#b91c1c' : '#475569', marginTop: 2 }}>
                            {csvFailed.toLocaleString()}
                          </div>
                          <div style={{ fontSize: 10.5, color: csvFailed > 0 ? '#dc2626' : '#94a3b8', marginTop: 1 }}>Errors</div>
                        </div>

                        {/* Duplicate Records */}
                        <div
                          style={{
                            background: csvDuplicates > 0 ? '#fffbeb' : '#f8fafc',
                            border: `1px solid ${csvDuplicates > 0 ? '#fde68a' : '#e2e8f0'}`,
                            borderRadius: 8,
                            padding: '10px 8px',
                            textAlign: 'center',
                          }}
                        >
                          <div style={{ fontSize: 10.5, color: csvDuplicates > 0 ? '#92400e' : '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>
                            Duplicates
                          </div>
                          <div style={{ fontSize: 18, fontWeight: 800, color: csvDuplicates > 0 ? '#d97706' : '#475569', marginTop: 2 }}>
                            {csvDuplicates.toLocaleString()}
                          </div>
                          <div style={{ fontSize: 10.5, color: csvDuplicates > 0 ? '#f59e0b' : '#94a3b8', marginTop: 1 }}>Skipped/Existing</div>
                        </div>

                        {/* Pending Rows */}
                        <div
                          style={{
                            background: csvPending > 0 ? '#eff6ff' : '#f8fafc',
                            border: `1px solid ${csvPending > 0 ? '#bfdbfe' : '#e2e8f0'}`,
                            borderRadius: 8,
                            padding: '10px 8px',
                            textAlign: 'center',
                          }}
                        >
                          <div style={{ fontSize: 10.5, color: csvPending > 0 ? '#1e40af' : '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>
                            Remaining
                          </div>
                          <div style={{ fontSize: 18, fontWeight: 800, color: csvPending > 0 ? '#2563eb' : '#475569', marginTop: 2 }}>
                            {csvPending.toLocaleString()}
                          </div>
                          <div style={{ fontSize: 10.5, color: csvPending > 0 ? '#3b82f6' : '#94a3b8', marginTop: 1 }}>Awaiting</div>
                        </div>
                      </div>
                    </div>

                    {/* ── CASE 2: EMAIL SEND STATUS ── */}
                    <div
                      style={{
                        background: '#ffffff',
                        border: '1px solid #e2e8f0',
                        borderRadius: 12,
                        padding: '18px 20px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontSize: 16 }}>📧</span>
                          <div>
                            <span style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>
                              Email Notifications Send Status
                            </span>
                            <span style={{ fontSize: 12, color: '#64748b', marginLeft: 8 }}>
                              ({emailSent.toLocaleString()} of {emailTotal.toLocaleString()} emails sent)
                            </span>
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span
                            style={{
                              fontSize: 18,
                              fontWeight: 800,
                              color: (isCompleted || emailPercent === 100) ? '#16a34a' : '#7c3aed',
                            }}
                          >
                            {emailPercent}%
                          </span>
                        </div>
                      </div>

                      {/* Progress Bar */}
                      <div
                        style={{
                          width: '100%',
                          height: 12,
                          background: '#e2e8f0',
                          borderRadius: 6,
                          overflow: 'hidden',
                          marginBottom: 16,
                          position: 'relative',
                        }}
                      >
                        <div
                          style={{
                            width: `${emailPercent}%`,
                            height: '100%',
                            background: (isCompleted || emailPercent === 100)
                              ? 'linear-gradient(90deg, #10b981 0%, #059669 100%)'
                              : 'linear-gradient(90deg, #8b5cf6 0%, #6366f1 100%)',
                            borderRadius: 6,
                            transition: 'width 0.4s ease-in-out',
                          }}
                        />
                      </div>

                      {/* 4 Cards: Total, Delivered/Sent, Failed, Pending */}
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
                        {/* Total Emails */}
                        <div
                          style={{
                            background: '#f8fafc',
                            border: '1px solid #e2e8f0',
                            borderRadius: 8,
                            padding: '10px 12px',
                            textAlign: 'center',
                          }}
                        >
                          <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>
                            Total Emails
                          </div>
                          <div style={{ fontSize: 20, fontWeight: 800, color: '#0f172a', marginTop: 2 }}>
                            {emailTotal.toLocaleString()}
                          </div>
                          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1 }}>Queued for delivery</div>
                        </div>

                        {/* Delivered / Sent */}
                        <div
                          style={{
                            background: '#ecfdf5',
                            border: '1px solid #a7f3d0',
                            borderRadius: 8,
                            padding: '10px 12px',
                            textAlign: 'center',
                          }}
                        >
                          <div style={{ fontSize: 11, color: '#065f46', fontWeight: 600, textTransform: 'uppercase' }}>
                            Delivered / Sent
                          </div>
                          <div style={{ fontSize: 20, fontWeight: 800, color: '#047857', marginTop: 2 }}>
                            {emailSent.toLocaleString()}
                          </div>
                          <div style={{ fontSize: 11, color: '#10b981', marginTop: 1 }}>Successfully sent</div>
                        </div>

                        {/* Failed Emails */}
                        <div
                          style={{
                            background: emailFailed > 0 ? '#fef2f2' : '#f8fafc',
                            border: `1px solid ${emailFailed > 0 ? '#fecaca' : '#e2e8f0'}`,
                            borderRadius: 8,
                            padding: '10px 12px',
                            textAlign: 'center',
                          }}
                        >
                          <div style={{ fontSize: 11, color: emailFailed > 0 ? '#991b1b' : '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>
                            Failed Emails
                          </div>
                          <div style={{ fontSize: 20, fontWeight: 800, color: emailFailed > 0 ? '#b91c1c' : '#475569', marginTop: 2 }}>
                            {emailFailed.toLocaleString()}
                          </div>
                          <div style={{ fontSize: 11, color: emailFailed > 0 ? '#dc2626' : '#94a3b8', marginTop: 1 }}>Bounced / Failed</div>
                        </div>

                        {/* Pending Emails */}
                        <div
                          style={{
                            background: emailPending > 0 ? '#fffbeb' : '#f8fafc',
                            border: `1px solid ${emailPending > 0 ? '#fde68a' : '#e2e8f0'}`,
                            borderRadius: 8,
                            padding: '10px 12px',
                            textAlign: 'center',
                          }}
                        >
                          <div style={{ fontSize: 11, color: emailPending > 0 ? '#92400e' : '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>
                            Pending Emails
                          </div>
                          <div style={{ fontSize: 20, fontWeight: 800, color: emailPending > 0 ? '#d97706' : '#475569', marginTop: 2 }}>
                            {emailPending.toLocaleString()}
                          </div>
                          <div style={{ fontSize: 11, color: emailPending > 0 ? '#f59e0b' : '#94a3b8', marginTop: 1 }}>Awaiting delivery</div>
                        </div>
                      </div>
                    </div>

                    {/* ── Failed Records / Error Summary (If Any) ── */}
                    {(upload.error_summary || csvFailed > 0 || emailFailed > 0) && (
                      <div
                        style={{
                          background: '#fff7ed',
                          border: '1px solid #fed7aa',
                          borderRadius: 10,
                          padding: '12px 16px',
                        }}
                      >
                        <div style={{ fontSize: 13, fontWeight: 700, color: '#9a3412', marginBottom: 4 }}>
                          Failure & Error Information
                        </div>
                        <div style={{ fontSize: 12, color: '#7c2d12', lineHeight: 1.5 }}>
                          {upload.error_summary
                            ? upload.error_summary
                            : `${csvFailed} CSV rows and ${emailFailed} emails encountered issues during processing. Check email bounce records and CSV row formatting.`}
                        </div>
                      </div>
                    )}

                    {/* ── ALL UPLOADED CSV FILES CARDS ── */}
                    <div
                      style={{
                        background: '#ffffff',
                        border: '1px solid #e2e8f0',
                        borderRadius: 12,
                        padding: '18px 20px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 14,
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontSize: 18 }}>📂</span>
                          <div>
                            <span style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>
                              All Uploaded CSV Files ({csvJobs.length})
                            </span>
                            <span style={{ fontSize: 12, color: '#64748b', marginLeft: 8 }}>
                              Control, pause, resume or delete each CSV upload card individually
                            </span>
                          </div>
                        </div>

                        {/* Search & Filter Controls for Cards */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <input
                            type="text"
                            placeholder="Search file name..."
                            value={csvCardSearch}
                            onChange={(e) => setCsvCardSearch(e.target.value)}
                            style={{
                              padding: '5px 10px',
                              borderRadius: 6,
                              border: '1px solid #cbd5e1',
                              fontSize: 12,
                              outline: 'none',
                              width: 150,
                            }}
                          />
                          <select
                            value={csvCardFilter}
                            onChange={(e) => setCsvCardFilter(e.target.value)}
                            style={{
                              padding: '5px 8px',
                              borderRadius: 6,
                              border: '1px solid #cbd5e1',
                              fontSize: 12,
                              outline: 'none',
                              background: '#ffffff',
                            }}
                          >
                            <option value="all">All Files ({csvJobs.length})</option>
                            <option value="processing">Processing / Queued ({csvJobs.filter(j => ['processing', 'queued', 'uploading'].includes(String(j.status || '').toLowerCase())).length})</option>
                            <option value="completed">Completed ({csvJobs.filter(j => ['done', 'completed', 'finished'].includes(String(j.status || '').toLowerCase())).length})</option>
                            <option value="paused">Paused / Cancelled ({csvJobs.filter(j => ['paused', 'cancelled'].includes(String(j.status || '').toLowerCase())).length})</option>
                            <option value="failed">Failed ({csvJobs.filter(j => String(j.status || '').toLowerCase() === 'failed').length})</option>
                          </select>
                        </div>
                      </div>

                      {/* Cards Grid */}
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))',
                          gap: 16,
                          padding: '2px',
                        }}
                      >
                        {filteredCsvJobs.length === 0 ? (
                          <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '32px 16px', color: '#94a3b8', fontSize: 13.5, background: '#f8fafc', borderRadius: 8, border: '1px dashed #cbd5e1' }}>
                            No CSV file uploads match the selected filter.
                          </div>
                        ) : (
                          filteredCsvJobs.map((job) => {
                            const jStatus = String(job.status || 'queued').toLowerCase();
                            const jCompleted = ['done', 'completed', 'finished'].includes(jStatus);
                            const jProcessing = ['processing', 'queued', 'uploading'].includes(jStatus);
                            const jPaused = ['cancelled', 'paused'].includes(jStatus);
                            const jFailed = jStatus === 'failed';
                            const isSelected = job.upload_id === selectedUploadId;

                            const jTotalRows = Number(job.total_rows || 0);
                            const jSavedRows = Number(job.successful_rows || 0);
                            const jFailedRows = Number(job.failed_rows || 0);
                            const jPendingRows = Number(job.pending_rows !== undefined ? job.pending_rows : Math.max(0, jTotalRows - (jSavedRows + jFailedRows)));

                            const jEmailTotal = Number(job.email_total || 0);
                            const jEmailSent = Number(job.email_sent || 0);
                            const jEmailFailed = Number(job.email_failed || 0);
                            const jEmailPending = Number(job.email_pending !== undefined ? job.email_pending : Math.max(0, jEmailTotal - (jEmailSent + jEmailFailed)));

                            let jPercent = 0;
                            if (jTotalRows > 0) {
                              if (jCompleted) jPercent = 100;
                              else jPercent = Math.min(100, Math.max(0, Math.round(((jSavedRows + jFailedRows) / jTotalRows) * 100)));
                            } else if (jCompleted) {
                              jPercent = 100;
                            }

                            const jDate = job.created_at ? new Date(job.created_at).toLocaleString() : 'Recent';

                            return (
                              <div
                                key={job.upload_id}
                                onClick={() => {
                                  setSelectedUploadId(job.upload_id);
                                  fetchCsvJobDetails(job.upload_id);
                                }}
                                style={{
                                  background: isSelected ? '#f8faff' : '#ffffff',
                                  border: isSelected ? '2px solid #2563eb' : '1px solid #e2e8f0',
                                  borderRadius: 12,
                                  padding: '16px',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: 12,
                                  boxShadow: isSelected ? '0 4px 16px rgba(37,99,235,0.12)' : '0 1px 4px rgba(0,0,0,0.04)',
                                  transition: 'all 0.2s ease',
                                  cursor: 'pointer',
                                }}
                              >
                                {/* Card Header */}
                                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div
                                      style={{
                                        fontSize: 14,
                                        fontWeight: 700,
                                        color: isSelected ? '#1d4ed8' : '#0f172a',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                      }}
                                      title={job.filename || 'Upload'}
                                    >
                                      📄 {job.filename || 'Upload'}
                                    </div>
                                    <div style={{ fontSize: 11.5, color: '#64748b', marginTop: 3 }}>
                                      Uploaded: {jDate}
                                    </div>
                                  </div>

                                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                                    {isSelected && (
                                      <span
                                        style={{
                                          fontSize: 10,
                                          fontWeight: 700,
                                          padding: '3px 7px',
                                          borderRadius: 6,
                                          background: '#2563eb',
                                          color: '#ffffff',
                                        }}
                                      >
                                        Active
                                      </span>
                                    )}
                                    <span
                                      style={{
                                        fontSize: 10.5,
                                        fontWeight: 700,
                                        padding: '3px 9px',
                                        borderRadius: 10,
                                        textTransform: 'uppercase',
                                        background: jCompleted ? '#dcfce7' : jFailed ? '#fee2e2' : jPaused ? '#fef3c7' : '#dbeafe',
                                        color: jCompleted ? '#166534' : jFailed ? '#991b1b' : jPaused ? '#92400e' : '#1d4ed8',
                                        whiteSpace: 'nowrap',
                                      }}
                                    >
                                      {job.status || 'Queued'}
                                    </span>
                                  </div>
                                </div>

                                {/* Progress Bar */}
                                <div>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11.5, fontWeight: 600, color: '#475569', marginBottom: 5 }}>
                                    <span>Database Save Progress</span>
                                    <span style={{ color: jCompleted ? '#16a34a' : jFailed ? '#dc2626' : '#2563eb' }}>{jPercent}%</span>
                                  </div>
                                  <div style={{ width: '100%', height: 7, background: '#e2e8f0', borderRadius: 4, overflow: 'hidden' }}>
                                    <div
                                      style={{
                                        width: `${jPercent}%`,
                                        height: '100%',
                                        background: jCompleted
                                          ? '#10b981'
                                          : jFailed
                                            ? '#ef4444'
                                            : 'linear-gradient(90deg, #3b82f6, #2563eb)',
                                        borderRadius: 4,
                                        transition: 'width 0.3s ease',
                                      }}
                                    />
                                  </div>
                                </div>

                                {/* CSV Rows Stats */}
                                <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px' }}>
                                  <div style={{ fontSize: 11.5, fontWeight: 700, color: '#334155', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <span>📊</span> CSV Database Save:
                                  </div>
                                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 4, textAlign: 'center' }}>
                                    <div>
                                      <div style={{ fontSize: 10, color: '#64748b', fontWeight: 600 }}>TOTAL</div>
                                      <div style={{ fontSize: 13.5, fontWeight: 700, color: '#0f172a', marginTop: 2 }}>{jTotalRows.toLocaleString()}</div>
                                    </div>
                                    <div>
                                      <div style={{ fontSize: 10, color: '#059669', fontWeight: 600 }}>SAVE DB</div>
                                      <div style={{ fontSize: 13.5, fontWeight: 700, color: '#059669', marginTop: 2 }}>{jSavedRows.toLocaleString()}</div>
                                    </div>
                                    <div>
                                      <div style={{ fontSize: 10, color: jFailedRows > 0 ? '#dc2626' : '#64748b', fontWeight: 600 }}>FAILED</div>
                                      <div style={{ fontSize: 13.5, fontWeight: 700, color: jFailedRows > 0 ? '#dc2626' : '#64748b', marginTop: 2 }}>{jFailedRows.toLocaleString()}</div>
                                    </div>
                                    <div>
                                      <div style={{ fontSize: 10, color: '#2563eb', fontWeight: 600 }}>PENDING</div>
                                      <div style={{ fontSize: 13.5, fontWeight: 700, color: '#2563eb', marginTop: 2 }}>{jPendingRows.toLocaleString()}</div>
                                    </div>
                                  </div>
                                </div>

                                {/* Email Notifications Stats */}
                                <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px' }}>
                                  <div style={{ fontSize: 11.5, fontWeight: 700, color: '#334155', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <span>📧</span> Email Notifications:
                                  </div>
                                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 4, textAlign: 'center' }}>
                                    <div>
                                      <div style={{ fontSize: 10, color: '#64748b', fontWeight: 600 }}>TOTAL</div>
                                      <div style={{ fontSize: 13.5, fontWeight: 700, color: '#0f172a', marginTop: 2 }}>{jEmailTotal.toLocaleString()}</div>
                                    </div>
                                    <div>
                                      <div style={{ fontSize: 10, color: '#059669', fontWeight: 600 }}>SENT</div>
                                      <div style={{ fontSize: 13.5, fontWeight: 700, color: '#059669', marginTop: 2 }}>{jEmailSent.toLocaleString()}</div>
                                    </div>
                                    <div>
                                      <div style={{ fontSize: 10, color: jEmailFailed > 0 ? '#dc2626' : '#64748b', fontWeight: 600 }}>FAILED</div>
                                      <div style={{ fontSize: 13.5, fontWeight: 700, color: jEmailFailed > 0 ? '#dc2626' : '#64748b', marginTop: 2 }}>{jEmailFailed.toLocaleString()}</div>
                                    </div>
                                    <div>
                                      <div style={{ fontSize: 10, color: '#d97706', fontWeight: 600 }}>PENDING</div>
                                      <div style={{ fontSize: 13.5, fontWeight: 700, color: '#d97706', marginTop: 2 }}>{jEmailPending.toLocaleString()}</div>
                                    </div>
                                  </div>
                                </div>

                                {/* Action Buttons per Card */}
                                <div
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 6,
                                    borderTop: '1px solid #e2e8f0',
                                    paddingTop: 12,
                                    marginTop: 'auto',
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                    {/* Resume / Restart */}
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleResumeUpload(job.upload_id);
                                      }}
                                      disabled={resumingUploadId === job.upload_id}
                                      style={{
                                        padding: '6px 12px',
                                        borderRadius: 6,
                                        border: '1px solid #2563eb',
                                        background: '#2563eb',
                                        color: '#ffffff',
                                        fontSize: 12,
                                        fontWeight: 600,
                                        cursor: resumingUploadId === job.upload_id ? 'not-allowed' : 'pointer',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 5,
                                        boxShadow: '0 1px 2px rgba(37,99,235,0.2)',
                                      }}
                                      title="Resume or restart this CSV upload"
                                    >
                                      ▶ {resumingUploadId === job.upload_id ? 'Starting...' : jProcessing ? 'Restart' : jPaused ? 'Resume' : jFailed ? 'Retry' : 'Restart'}
                                    </button>

                                    {/* Stop / Pause */}
                                    {(jProcessing || jStatus === 'queued' || jStatus === 'uploading') && (
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleCancelUpload(job.upload_id);
                                        }}
                                        disabled={cancellingUploadId === job.upload_id}
                                        style={{
                                          padding: '6px 12px',
                                          borderRadius: 6,
                                          border: '1px solid #d97706',
                                          background: '#fffbeb',
                                          color: '#b45309',
                                          fontSize: 12,
                                          fontWeight: 600,
                                          cursor: cancellingUploadId === job.upload_id ? 'not-allowed' : 'pointer',
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          gap: 5,
                                        }}
                                        title="Pause or stop this CSV upload"
                                      >
                                        ⏸️ {cancellingUploadId === job.upload_id ? 'Pausing...' : 'Stop'}
                                      </button>
                                    )}

                                    {/* Delete */}
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteUpload(job.upload_id);
                                      }}
                                      disabled={deletingUploadId === job.upload_id}
                                      style={{
                                        padding: '6px 10px',
                                        borderRadius: 6,
                                        border: '1px solid #fecaca',
                                        background: '#ffffff',
                                        color: '#dc2626',
                                        fontSize: 12,
                                        fontWeight: 600,
                                        cursor: deletingUploadId === job.upload_id ? 'not-allowed' : 'pointer',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 4,
                                      }}
                                      title="Permanently delete this upload"
                                    >
                                      🗑️ {deletingUploadId === job.upload_id ? '...' : 'Delete'}
                                    </button>
                                  </div>

                                  {/* Select button */}
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSelectedUploadId(job.upload_id);
                                      fetchCsvJobDetails(job.upload_id);
                                    }}
                                    style={{
                                      padding: '6px 12px',
                                      borderRadius: 6,
                                      border: isSelected ? '1px solid #2563eb' : '1px solid #cbd5e1',
                                      background: isSelected ? '#eff6ff' : '#ffffff',
                                      color: isSelected ? '#1d4ed8' : '#334155',
                                      fontSize: 12,
                                      fontWeight: isSelected ? 700 : 500,
                                      cursor: 'pointer',
                                    }}
                                  >
                                    {isSelected ? '✓ Active' : 'Details'}
                                  </button>
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>
                    </div>

                    {/* ── CASE 3: UPLOADED RECORDS REVIEW & REJECTION ACTIONS ── */}
                    <div
                      style={{
                        background: '#ffffff',
                        border: '1px solid #e2e8f0',
                        borderRadius: 12,
                        padding: '16px 18px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 12,
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontSize: 16 }}>📋</span>
                          <div>
                            <span style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>
                              Batch Upload Records Review
                            </span>
                            <span style={{ fontSize: 12, color: '#64748b', marginLeft: 8 }}>
                              ({filteredRecords.length} of {allRecords.length} records shown)
                            </span>
                          </div>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <input
                            placeholder="Filter email / row..."
                            value={csvRecordSearch}
                            onChange={(e) => setCsvRecordSearch(e.target.value)}
                            style={{
                              padding: '5px 10px',
                              borderRadius: 6,
                              border: '1px solid #cbd5e1',
                              fontSize: 12,
                              outline: 'none',
                              width: 150,
                            }}
                          />
                          <select
                            value={csvRecordStatusFilter}
                            onChange={(e) => setCsvRecordStatusFilter(e.target.value)}
                            style={{
                              padding: '5px 8px',
                              borderRadius: 6,
                              border: '1px solid #cbd5e1',
                              fontSize: 12,
                              background: '#fff',
                              outline: 'none',
                            }}
                          >
                            <option value="all">All ({allRecords.length})</option>
                            <option value="completed">Saved ({allRecords.filter(r => r.status === 'completed').length})</option>
                            <option value="failed">Failed ({allRecords.filter(r => r.status === 'failed').length})</option>
                            <option value="duplicate">Duplicates ({allRecords.filter(r => (r.error_message || '').toLowerCase().includes('duplicate') || r.status === 'skipped').length})</option>
                          </select>

                          {csvFailed > 0 && (
                            <button
                              onClick={() => handleResumeUpload(upload.upload_id || selectedUploadId)}
                              disabled={resumingUploadId === (upload.upload_id || selectedUploadId)}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 5,
                                padding: '5px 12px',
                                borderRadius: 6,
                                border: '1px solid #2563eb',
                                background: '#2563eb',
                                color: '#ffffff',
                                fontSize: 12,
                                fontWeight: 600,
                                cursor: resumingUploadId ? 'not-allowed' : 'pointer',
                              }}
                              title="Retry failed records in this batch"
                            >
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
                                <path d="M23 4v6h-6M1 20v-6h6" />
                                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
                              </svg>
                              {resumingUploadId === (upload.upload_id || selectedUploadId) ? 'Retrying...' : 'Retry Failed'}
                            </button>
                          )}
                        </div>
                      </div>

                      <div style={{ maxHeight: 260, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: 8 }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                          <thead>
                            <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', position: 'sticky', top: 0, zIndex: 2 }}>
                              <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 700, color: '#475569', width: 50 }}>Row</th>
                              <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 700, color: '#475569' }}>Email / Target</th>
                              <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 700, color: '#475569' }}>Consent Type</th>
                              <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 700, color: '#475569', width: 90 }}>Status</th>
                              <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 700, color: '#475569' }}>Reason / Details</th>
                              <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 700, color: '#475569', width: 110 }}>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredRecords.length === 0 ? (
                              <tr>
                                <td colSpan={6} style={{ padding: 24, textAlign: 'center', color: '#94a3b8' }}>
                                  {allRecords.length === 0 ? 'No record logs recorded for this batch yet.' : 'No records match the active filter.'}
                                </td>
                              </tr>
                            ) : (
                              filteredRecords.map((rec, index) => {
                                const isRecFailed = rec.status === 'failed';
                                const isRecDup = (rec.error_message || '').toLowerCase().includes('duplicate') || rec.status === 'skipped';
                                const isRecCompleted = rec.status === 'completed';
                                const canReject = isRecCompleted || rec.consent_id || (!isRecFailed && rec.email);

                                return (
                                  <tr key={rec.id || index} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                    <td style={{ padding: '8px 10px', fontWeight: 600, color: '#64748b' }}>
                                      {rec.row_number || index + 1}
                                    </td>
                                    <td style={{ padding: '8px 10px', fontWeight: 600, color: '#0f172a' }}>
                                      {rec.email ? maskEmail(rec.email) : (rec.username || '—')}
                                    </td>
                                    <td style={{ padding: '8px 10px', color: '#475569' }}>
                                      {rec.consent_type || '—'}
                                    </td>
                                    <td style={{ padding: '8px 10px' }}>
                                      <span
                                        style={{
                                          display: 'inline-block',
                                          padding: '2px 8px',
                                          borderRadius: 10,
                                          fontSize: 10.5,
                                          fontWeight: 700,
                                          textTransform: 'uppercase',
                                          background: isRecCompleted ? '#dcfce7' : isRecDup ? '#fef3c7' : isRecFailed ? '#fee2e2' : '#f1f5f9',
                                          color: isRecCompleted ? '#166534' : isRecDup ? '#92400e' : isRecFailed ? '#991b1b' : '#475569',
                                        }}
                                      >
                                        {isRecCompleted ? 'Saved' : isRecDup ? 'Duplicate' : isRecFailed ? 'Failed' : rec.status}
                                      </span>
                                    </td>
                                    <td
                                      style={{
                                        padding: '8px 10px',
                                        color: isRecFailed ? '#991b1b' : isRecDup ? '#92400e' : '#64748b',
                                        fontSize: 11.5,
                                        maxWidth: 220,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                      }}
                                      title={rec.error_message || ''}
                                    >
                                      {rec.error_message || (isRecCompleted ? 'Successfully saved in database' : '—')}
                                    </td>
                                    <td style={{ padding: '8px 10px', textAlign: 'center' }}>
                                      {canReject ? (
                                        <button
                                          onClick={() => handleRejectRecord(rec)}
                                          disabled={rejectingRecordId === (rec.id || rec.row_number)}
                                          style={{
                                            padding: '4px 9px',
                                            borderRadius: 6,
                                            border: '1px solid #fca5a5',
                                            background: '#fef2f2',
                                            color: '#b91c1c',
                                            fontSize: 11,
                                            fontWeight: 600,
                                            cursor: rejectingRecordId === (rec.id || rec.row_number) ? 'not-allowed' : 'pointer',
                                          }}
                                          title="Reject / Revoke this record from database"
                                        >
                                          {rejectingRecordId === (rec.id || rec.row_number) ? 'Revoking...' : 'Reject / Revoke'}
                                        </button>
                                      ) : (
                                        <span style={{ fontSize: 11, color: '#94a3b8' }}>—</span>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Modal Footer */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '12px 22px',
                  background: '#f8fafc',
                  borderTop: '1px solid #e2e8f0',
                }}
              >
                <div style={{ fontSize: 12, color: '#64748b' }}>
                  {isTerminal
                    ? 'Processing completed. Counts are finalized.'
                    : 'Auto-updating status every 3 seconds while modal is open.'}
                </div>
                <button
                  onClick={() => setShowCsvStatusModal(false)}
                  style={{
                    padding: '8px 20px',
                    borderRadius: 8,
                    border: '1px solid #cbd5e1',
                    background: '#ffffff',
                    color: '#334155',
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#f1f5f9'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = '#ffffff'; }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div >
  );
}